<script setup lang="ts">
import { ref, computed, reactive, onMounted, onUnmounted } from 'vue'
import { api, type HoldingsStatusData, type EnrichedSynthNavData, type EquityPricesResponse, type PriceData, type SignalData, type JhSignalData, type GoldHoldingsResponse, type GoldKtLatestResponse, type HoldingsActiveEntry, type StrikeTotalResponse } from '../api/client'
import { usePortfolio } from '../composables/usePortfolio'
import { useFundEnablement } from '../composables/useFundEnablement'
import { fundForPortfolio } from './nav/fundConfig'
import { resolveMarketReturn } from '../utils/marketReturn'
import { isInActualEtRth, isSessionAnchorOvertaken } from '../utils/etRth'
import { mapGoldHoldings, type GoldRow, type GoldEnrichedInput } from '../lib/goldHoldingsRows'
import { deriveGoldOverlay, formatOverlayPct } from '../utils/goldOverlay'
import { deriveHoldingsHybrid, type HoldingsLatestResponse, type HoldingsHybridRow } from '../utils/holdingsHybrid'
import { deriveStrikeTotal, NOT_PROMOTED } from '../utils/strikeTotal'
import ImrBreakdownPopover from './ImrBreakdownPopover.vue'
import PreStrikeBadge from './HoldingsPanel/PreStrikeBadge.vue'

const { portfolio } = usePortfolio()
// 2026-11-02: /synth-nav/enriched is a per-user fund route — wait for /me before
// the first poll so no fail-open first tick goes out (see Dashboard.pollIntegrity).
const { whenLoaded } = useFundEnablement()
// Option C PR 3 (2026-10-11): the page's registry fund; funds this panel
// has no holdings view for fall back to BGA, as before.
const HOLDINGS_FUNDS = ['BGA', 'JH', 'GOLD', 'GGE'] as const
type HoldingsFund = typeof HOLDINGS_FUNDS[number]
const fund = computed<HoldingsFund>(() => {
  const f = fundForPortfolio(portfolio.value)
  return (HOLDINGS_FUNDS as readonly string[]).includes(f) ? f as HoldingsFund : 'BGA'
})
const isGold = computed(() => fund.value === 'GOLD')
const isGge = computed(() => fund.value === 'GGE')
// Fund-neutral holdings-hybrid capability (2026-09-08, PR-C Task C2 — GGE
// per-holding live-hybrid arc §11.3): true for any fund with a live
// per-holding hybrid publisher (GOLD's 2026-09-04 arc, GGE's port of it).
// Gates the hybrid dot override / ADJ-cell content / "next-day indication"
// footer below — see holdingsHybridLatest/holdingsHybrid.
const hasHoldingsHybrid = computed(() => isGold.value || isGge.value)
// Narrowed alias for the pollData-path endpoints (holdingsActive,
// enrichedSynthNav, signal) — only read inside pollData, which onMounted
// never invokes when isGold is true. GGE (promotion Phase 4, 2026-08-18)
// rides this path: /holdings/active?fund=gge decodes holdings:gge:active
// and enrichedSynthNav('GGE') reads synth_nav:gge:enriched. holdingsStatus
// (F2 review fix, 2026-08-16) polls with the real fund.value — GOLD gets
// its own status/rebalance banner instead of being coerced to BGA's.
const nonGoldFund = computed<'BGA' | 'JH' | 'GGE'>(() =>
  fund.value === 'JH' ? 'JH' : fund.value === 'GGE' ? 'GGE' : 'BGA')

const status = ref<HoldingsStatusData | null>(null)
const enriched = ref<EnrichedSynthNavData | null>(null)
const holdings = ref<HoldingsActiveEntry[] | null>(null)
// JH-only: signal payload for the MR pill (mr_zscore, mr_tilt_bps).
// BGA's IMR breadth pill reads from enriched (BGA-only); JH has no
// enriched data but the Python jh_signal payload carries mean-reversion
// telemetry that we surface here as the JH-side equivalent.
const jhSignal = ref<JhSignalData | null>(null)
const equityPrices = ref<EquityPricesResponse | null>(null)
// GOLD-only: raw composition snapshot from /api/gold/holdings, merged with
// live equityPrices into GoldRow[] via mapGoldHoldings (Task 1). This mode's
// poll behaviour keeps the last good snapshot on a failed fetch rather than
// blanking the table.
const goldResp = ref<GoldHoldingsResponse | null>(null)
// GOLD-only: hybrid-cadence telemetry from synth_nav:gold:enriched (Task 5,
// 2026-08-15 spec) — daily betas/z + stale_days, polled every 5s alongside
// fetchGoldHoldings so RET%/DIR track live prices between daily publishes.
const goldEnriched = ref<EnrichedSynthNavData | null>(null)
// GOLD-only: gold K_t live overlay bridge (2026-09-02) — /api/gold/kt-latest
// polled on the same 5-min cadence GoldSignalPanel uses (loadKtLatest), so
// this footer, the signal panel's overlay line, and GoldSparklineBar's
// combined return all agree with each other and with the P&L bridge.
const ktLatest = ref<GoldKtLatestResponse | null>(null)
const goldOverlay = computed(() => deriveGoldOverlay(ktLatest.value))
async function loadKtLatest(): Promise<void> {
  try { ktLatest.value = await api.goldKtLatest() } catch { /* keep last */ }
}
const overlayFooterFrac = computed(() => formatOverlayPct(goldOverlay.value.overlayFrac))
const overlayFooterBeta = computed(() =>
  goldOverlay.value.beta != null ? goldOverlay.value.beta.toFixed(3) : '—')
const overlayFooterGoldMove = computed(() => formatOverlayPct(goldOverlay.value.goldMoveFrac))
const overlayFooterCombined = computed(() => formatOverlayPct(goldOverlay.value.combinedFrac))

// Fund-neutral: holdings-hybrid display bridge (2026-09-04 gold-evening-
// hybrid PR C Task 10; ported to GGE 2026-09-08, PR-C Task C2, spec §11.3).
// /api/gold/holdings-latest (GOLD) / /api/gge/holdings-latest (GGE) is a
// thin passthrough of the respective synth_nav:<fund>:holdings_latest key,
// published every 60s during the relevant live session by
// <Fund>HoldingsHybridService — per-holding live/proxy state, plus an
// aggregate next-day indication. `deriveHoldingsHybrid` is the single
// source of truth: `active` only when the payload has holdings AND is < 15
// minutes old. When active, this overrides the ticker cell's dot
// (live/prxy) and the ADJ cell for proxied rows (GOLD also feeds the
// shared SRC cell — Task 2, 2026-09-04, see goldHoldingsRows.ts's
// hybrid-aware src mapping, gold-only); the "next-day indication" footer
// takes precedence over the #329 gold-only K_t overlay footer.
const holdingsHybridLatest = ref<HoldingsLatestResponse | null>(null)
const holdingsHybrid = computed(() => deriveHoldingsHybrid(holdingsHybridLatest.value))
async function loadHoldingsHybridLatest(): Promise<void> {
  // BGA/JH strike-anchor promotion Phase 1 (2026-10-24): the strike-total
  // poll runs for GGE/BGA/JH on the same cadence, BEFORE the hybrid
  // early-return (BGA/JH have no hybrid). Gold never calls it
  // (strikeTotalFund is null) — HoldingsPanel.gold.test.ts enforces this.
  if (strikeTotalFund.value) {
    try {
      strikeTotalResp.value = await api.strikeTotal(strikeTotalFund.value)
    } catch {
      strikeTotalResp.value = null
    }
  }
  // Fund-scoping invariant (matches loadKtLatest / #329): BGA and JH MUST
  // NEVER call either hybrid endpoint. HoldingsPanel.bga.test.ts /
  // HoldingsPanel.gge.test.ts enforce this.
  if (!hasHoldingsHybrid.value) return
  try {
    holdingsHybridLatest.value = isGold.value
      ? await api.goldHoldingsLatest()
      : await api.ggeHoldingsLatest()
  } catch { /* keep last */ }
}
// Task 6 (2026-10-24, GGE strike-anchor promotion arc): the TOTAL-row
// view-model. `promoted` is true only when the server returns
// `mode: 'shadow'` AND the payload is available — deriveStrikeTotal is the
// single source of truth (utils/strikeTotal.ts; Task 5). Depends on
// `now.value` so the stale-asOfUtc amber branch re-evaluates on each 60s
// tick. NOT_PROMOTED for gold (strikeTotalFund null) so the template branch and the
// hybrid footer suppression stay inert.
const strikeTotalResp = ref<StrikeTotalResponse | null>(null)
// Funds whose TOTAL row can be promoted (gold never: it has its own K_t arc).
const strikeTotalFund = computed<'gge' | 'bga' | 'jh' | null>(() =>
  fund.value === 'GGE' ? 'gge' : fund.value === 'BGA' ? 'bga' : fund.value === 'JH' ? 'jh' : null)
const strikeTotal = computed(() => strikeTotalFund.value ? deriveStrikeTotal(strikeTotalResp.value, now.value) : NOT_PROMOTED)
// Format the hybrid footer's signed percentage (aggregateIndicationFrac,
// null-safe). Matches formatOverlayPct's convention (2dp, explicit '+').
const hybridFooterAggregate = computed(() => formatOverlayPct(holdingsHybrid.value.aggregateFrac))
const hybridFooterCoveredPct = computed(() =>
  holdingsHybrid.value.coveredWeightFrac != null
    ? Math.round(holdingsHybrid.value.coveredWeightFrac * 100) + '%'
    : '—')
const hybridFooterLivePct = computed(() =>
  holdingsHybrid.value.liveWeightFrac != null
    ? Math.round(holdingsHybrid.value.liveWeightFrac * 100) + '%'
    : '—')

// "delayed Y%" in the hybrid footer — only when some weight is on delayed
// quotes (GOLD's Toronto-only names, 2026-10-15); empty otherwise (GGE).
const hybridFooterDelayedPct = computed(() => {
  const d = holdingsHybrid.value.delayedWeightFrac
  return d != null && d > 0 ? Math.round(d * 100) + '%' : null
})

// Per-row hybrid lookup: null when hybrid inactive or ticker not covered.
// Returns 'live' / 'delayed' / 'prxy' along with the adj% string for the ADJ cell.
function hybridRowFor(ticker: string): { state: HoldingsHybridRow['state']; adjText: string } | null {
  const view = holdingsHybrid.value
  if (!view.active) return null
  const row = view.rowsByTicker.get(ticker)
  if (!row) return null
  return { state: row.state, adjText: formatOverlayPct(row.adjFrac) }
}
// Ticker-cell dot for a hybrid row: green live, blue delayed (EODHD
// ~15 min quote, 2026-10-15), grey proxied.
function hybridDotClass(state: HoldingsHybridRow['state']): string {
  return state === 'live' ? 'dot-live' : state === 'delayed' ? 'dot-delayed' : 'dot-stale'
}
const loading = ref(true)
// showAllHoldings removed — always show all
const showSrcInfo = ref(false)
const sortBy = ref<'weight' | 'return' | 'contribution' | 'mr'>('weight')
const sortDir = ref<'desc' | 'asc'>('desc')
const showAdjInfo = ref(false)
const showMomInfo = ref(false)
const showImrBreakdown = ref(false)
let statusTimer: ReturnType<typeof setInterval> | null = null
let dataTimer: ReturnType<typeof setInterval> | null = null
let equityTimer: ReturnType<typeof setInterval> | null = null
let goldEnrichedTimer: ReturnType<typeof setInterval> | null = null
let ktTimer: ReturnType<typeof setInterval> | null = null
// GOLD-only: holdings-hybrid poll cadence — 60s while the tab is visible
// (the publisher publishes on the same 60s cadence during US sessions),
// silently skipped when the tab is hidden. Keeping this at a single 60s
// setInterval (no visibilitychange listener) matches the rest of this
// panel's polling idiom.
let holdingsHybridTimer: ReturnType<typeof setInterval> | null = null
// Interim pre-strike badge (PR A, 2026-09-16 gold/GGE strike-anchor arc,
// Task 1): this panel has no existing reactive server-time source (no
// useServerTime composable exists anywhere in src/), so `now` is a local
// wall-clock ref updated on a timer — same idiom as DealingPlanCard.vue's
// `now`/`tick()`. Only gold/GGE's PreStrikeBadge consumes it; a 60s cadence
// is plenty for an hour-granularity pre/post-strike check. The interval is
// only started for gold/GGE in onMounted below (Minor #1, review round 1)
// so BGA/JH don't pay for a tick nothing reads.
const now = ref(new Date())
let nowTimer: ReturnType<typeof setInterval> | null = null

// Track previous bid prices to detect tick direction
const prevBids: Record<string, number> = {}
// ticker -> 'up' | 'down' | null, with auto-clear timeout
const tickFlash = reactive<Record<string, string | null>>({})
const flashTimers: Record<string, ReturnType<typeof setTimeout>> = {}

// Is the enriched feed live?
const hasEnriched = computed(() =>
  enriched.value != null && enriched.value.holdings_detail?.length > 0
)

// GOLD-only: is the enriched telemetry payload live? Gates DIR/RET%/MOM —
// before first publish (or on fetch failure) the panel stays pixel-identical
// to the pre-Task-5 8-column layout.
const hasGoldEnriched = computed(() =>
  goldEnriched.value != null &&
  goldEnriched.value.available !== false &&
  (goldEnriched.value.holdings_detail?.length ?? 0) > 0
)

// liveTickers removed — no longer filtering

// Get live price for a ticker (or null)
function livePrice(ticker: string): PriceData | null {
  return equityPrices.value?.prices?.[ticker] ?? null
}

// GOLD-only: fetch the composition snapshot — on failure, keep the last
// good snapshot (behaviour ported into HoldingsPanel's GOLD mode).
async function fetchGoldHoldings() {
  try {
    const data = await api.goldHoldings()
    if (data) goldResp.value = data
  } catch {
    // Ignore — keep the last good snapshot
  } finally {
    loading.value = false
  }
}

// GOLD-only: fetch the hybrid-cadence telemetry payload (Task 5) — on
// failure, keep the last good payload (same pattern as fetchGoldHoldings)
// so a transient blip doesn't blank the DIR/RET%/MOM columns.
async function fetchGoldEnriched() {
  await whenLoaded()
  try {
    const data = await api.enrichedSynthNav('GOLD')
    if (data) goldEnriched.value = data
  } catch {
    // Ignore — keep the last good payload
  }
}

// GOLD-only: the enriched payload's holdings_detail keyed by ticker, reduced
// to the fields the mapper needs (prev_close/imr_zscore/imr_excluded_reason).
const goldEnrichedInput = computed<GoldEnrichedInput | null>(() => {
  if (!hasGoldEnriched.value) return null
  const detailByTicker: GoldEnrichedInput['detailByTicker'] = {}
  for (const d of goldEnriched.value!.holdings_detail) {
    detailByTicker[d.ticker] = {
      prev_close: d.prev_close,
      imr_zscore: d.imr_zscore,
      imr_excluded_reason: d.imr_excluded_reason,
      prev_return_pct: d.prev_return_pct ?? null,
      return_pct_gbp: d.return_pct_gbp ?? null,
      latest_close_native: d.latest_close_native ?? null,
      // JH-parity fix (2026-09-10): thread the completed-session anchors
      // through to mapGoldHoldings' resolveMarketReturn call.
      session_close: d.session_close ?? null,
      session_prev_close: d.session_prev_close ?? null,
    }
  }
  return { staleDays: goldEnriched.value!.stale_days ?? 0, detailByTicker }
})

// GOLD-only: rows merging the composition snapshot with live equity prices
// and (when available) the enriched telemetry payload. mapGoldHoldings
// hard-sorts weight-desc as a stable default; the panel layer (sortedGoldRows
// below) applies the interactive sortDir on top of it so the WT% header's
// click-to-sort behaves the same as the BGA/JH table.
const goldRows = computed<GoldRow[]>(() =>
  mapGoldHoldings(goldResp.value, equityPrices.value?.prices, goldEnrichedInput.value, holdingsHybrid.value.rowsByTicker))

// GOLD-only: goldRows re-ordered per the panel's shared sort direction state.
// WT% remains the default; RET%/MOM sortability (Task 2, 2026-09-04) reuses
// the same sortBy/sortDir refs BGA's `rows` computed drives, so a single
// toggleSort/sortIndicator pair works for both funds. desc matches goldRows'
// existing weight-desc default order; asc reverses it.
const sortedGoldRows = computed<GoldRow[]>(() => {
  const dir = sortDir.value === 'desc' ? -1 : 1
  const key = sortBy.value
  return [...goldRows.value].sort((a, b) => {
    const aVal = key === 'return' ? (a.ret_pct ?? 0)
      : key === 'mr' ? (a.imr_zscore ?? 0)
      : (a.weightPct ?? 0)
    const bVal = key === 'return' ? (b.ret_pct ?? 0)
      : key === 'mr' ? (b.imr_zscore ?? 0)
      : (b.weightPct ?? 0)
    return (aVal - bVal) * dir
  })
})

// GGE VEN/CCY columns (enriched-parity arc, §7C): venue/currency come from
// /api/holdings/active?fund=gge (the `holdings` ref, populated by pollData
// unconditionally for every non-gold fund) — NOT from enrichedSynthNav's
// holdings_detail, which carries no venue/currency fields. Keyed by ticker
// so the enriched-branch row builder below can thread them onto each row.
const activeByTicker = computed<Map<string, HoldingsActiveEntry>>(() => {
  const map = new Map<string, HoldingsActiveEntry>()
  for (const h of holdings.value ?? []) {
    if (h?.ticker) map.set(h.ticker, h)
  }
  return map
})

// Build rows: merge holdings + enriched + live equity prices
const allRows = computed(() => {
  if (hasEnriched.value) {
    return enriched.value!.holdings_detail.map(h => {
      const live = livePrice(h.ticker)
      const bid = live?.bid ?? null
      const ask = live?.ask ?? null
      const mid = live?.mid ?? null
      const spreadBps = (bid != null && ask != null && mid != null && mid > 0)
        ? Math.round((ask - bid) / mid * 10000)
        : h.spread_bps
      // Direction: live mid vs prev_close
      let direction: number | null = null
      const current = mid ?? h.proxy_price ?? h.after_hours_close
      if (current != null && h.prev_close != null) {
        direction = current > h.prev_close ? 1 : current < h.prev_close ? -1 : 0
      }
      // NQ delta adjustment: how much carry-forward was applied
      // Only meaningful when nq_ref differs meaningfully from nq_now (stale AV prices).
      // Live Pepperstone prices have nq_ref ≈ nq_now so ADJ ≈ 0 — show '--' instead.
      let nqAdj: number | null = null
      if (h.source === 'proxy' && h.nq_ref_at_fetch != null && enriched.value?.nq_price != null && h.nq_ref_at_fetch > 0) {
        const rawAdj = (enriched.value.nq_price / h.nq_ref_at_fetch) - 1
        // Only show ADJ if the adjustment is meaningful (>0.01% = 1bps)
        if (Math.abs(rawAdj) > 0.0001) {
          nqAdj = rawAdj
        }
      }
      // JH-parity fix (2026-09-10, PR-D of the enriched-parity arc): feed
      // the 60s hybrid publisher's price (when it covers this ticker) as
      // `cur` into resolveMarketReturn instead of overriding market_return
      // with the whole-live-move. This gives GGE the same clean two-window
      // decomposition JH has natively:
      //   mkt = session_close / session_prev_close - 1  (T-2 -> T-1)
      //   ah  = cur           / session_close       - 1  (T-1 -> now, when RTH closed)
      // The hybrid publisher's live-or-PRXY price is still what drives the
      // headline (via the `return_pct` cascade below) — it just does so
      // through the JH-shaped compound instead of a bare replacement.
      const hRow = holdingsHybrid.value.active
        ? holdingsHybrid.value.rowsByTicker.get(h.ticker)
        : null
      const hybridPrice = hRow?.price ?? null

      // Stale session-anchor gate (2026-09-24, paolo). GGE's only enriched
      // refresh is the per-venue US fire at the 16:00 ET close + 15min,
      // racing Alpha Vantage's settle of that session's bar. When it loses
      // the race, session_close/session_prev_close freeze on the PREVIOUS
      // session and nothing rebuilds them until the next US close. `cur` is
      // then a whole session ahead of `session_close`, so `ah` stops being
      // after-hours drift and instead swallows an entire regular session,
      // while `mkt` reports a stale session as if it were the current one.
      // Observed 2026-09-24: every US holding's anchors were the 09-22 pair
      // — XOM showed mkt +0.26% and ah +1.42%, when the 09-23 session it had
      // already traded through actually closed +1.59%.
      //
      // The payload's `as_of` cannot detect this (it is the MAX close date
      // across holdings — fresh off the HKEX names while the US names lag),
      // and a payload-level stale_days keyed on as_of would blank the panel
      // every morning, since GGE's as_of is legitimately yesterday until the
      // evening fetch. The per-holding session_close_date vs the hybrid
      // row's last real print is the signal that is actually about THIS
      // row's anchors. GGE-gated — BGA/JH publishers recompute their anchors
      // on every live tick and never carry a stale pair.
      //
      // isSessionAnchorOvertaken owns the comparison because a plain ET-date
      // difference is not enough: pre-market prints and live RTH both sit on
      // a later date than a perfectly good anchor. See its comment for the
      // two false positives that shape drove (2026-09-25).
      const anchorsOvertaken =
        fund.value === 'GGE' &&
        isSessionAnchorOvertaken(hRow?.lastRealAtUtc ?? null, h.session_close_date ?? null)

      const resolved = anchorsOvertaken
        ? { marketReturn: null, afterHoursReturn: null }
        : resolveMarketReturn({
            cur: hybridPrice ?? mid ?? h.proxy_price ?? h.after_hours_close,
            t1Close: h.prev_close_t1,
            prevClose: h.prev_close,
            sessionClose: h.session_close ?? null,
            sessionPrevClose: h.session_prev_close ?? null,
            // GGE's daily-cadence telemetry payload cannot carry a live RTH
            // flag — derive it client-side (etRth) so the closed-session
            // pill convention (session_close vs session_prev_close) applies
            // exactly like BGA/JH's publisher-supplied flag.
            rthOpen: enriched.value?.us_rth_open
              ?? (fund.value === 'GGE' ? isInActualEtRth() : undefined),
          })
      let market_return = resolved.marketReturn
      const ah_return = resolved.afterHoursReturn
      // _INDEX_PROXY has no per-ticker prices/closes — fall back to the
      // top-level NQ pair, preserving the pre-2026-07-16 behaviour.
      if (market_return == null && h.ticker === '_INDEX_PROXY') {
        const nqLive = enriched.value?.nq_price
        const nqT1 = enriched.value?.nas100_prev_close_t1
        if (nqLive && nqT1 && nqT1 > 0) market_return = (nqLive / nqT1) - 1
      }
      const activeMeta = activeByTicker.value.get(h.ticker)
      return {
        ticker: h.ticker,
        name: h.name,
        weightPct: h.weight * 100,
        prev_close: h.prev_close,
        // GGE headline RET% (JH-parity fix, 2026-09-10, PR-D): GGE's payload
        // never populates return_pct (see BuildEnrichedPayloadJson — always
        // null for GGE), so the headline cell must be built client-side from
        // the mkt/ah cascade above. Since GGE now shares JH's two-window
        // decomposition, the headline is the JH compound total
        // (1+mkt)*(1+ah) - 1 rather than a bare market_return — when only
        // one of mkt/ah is populated (e.g. RTH open, ah_return null; or,
        // less commonly, mkt_return null with ah_return present), the
        // cascade falls through to whichever value exists. BGA/JH are
        // untouched (fund-gated) since their payload's own return_pct is
        // already correct and market_return there is a wholly separate figure.
        return_pct: (fund.value === 'GGE' && (market_return != null || ah_return != null))
          ? (market_return != null && ah_return != null
              ? (1 + market_return) * (1 + ah_return) - 1
              : (market_return ?? ah_return))
          : h.return_pct,
        return_pct_gbp: h.return_pct_gbp ?? null,
        market_return,
        ah_return,
        contribution_pct: h.contribution_pct,
        source: h.source,
        bid: bid ?? h.proxy_price ?? h.after_hours_close ?? h.prev_close ?? enriched.value?.nq_price ?? null,
        ask: ask ?? h.proxy_price ?? h.after_hours_close ?? h.prev_close ?? enriched.value?.nq_price ?? null,
        spread_bps: spreadBps,
        direction,
        live: live != null,
        nqAdj,
        imrZ: h.imr_zscore ?? null,
        tier: h.tier ?? null,
        venue: activeMeta?.venue ?? null,
        currency: activeMeta?.currency ?? null,
      }
    })
  }
  if (!holdings.value) return []
  return [...holdings.value]
    .sort((a, b) => (b.weightPct ?? b.weight ?? 0) - (a.weightPct ?? a.weight ?? 0))
    .map(h => {
      const ticker = h.ticker || ''
      const live = livePrice(ticker)
      const bid = live?.bid ?? null
      const ask = live?.ask ?? null
      const mid = live?.mid ?? null
      const spreadBps = (bid != null && ask != null && mid != null && mid > 0)
        ? Math.round((ask - bid) / mid * 10000)
        : null
      return {
        ticker,
        name: h.name || '',
        weightPct: h.weightPct ?? h.weight ?? null,
        prev_close: null as number | null,
        return_pct: null as number | null,
        return_pct_gbp: null as number | null,
        market_return: null as number | null,
        ah_return: null as number | null,
        contribution_pct: null as number | null,
        source: 'unavailable',
        bid,
        ask,
        spread_bps: spreadBps,
        direction: null as number | null,
        live: live != null,
        nqAdj: null as number | null,
        imrZ: null as number | null,
        tier: null as string | null,
        venue: h.venue ?? null,
        currency: h.currency ?? null,
      }
    })
})

function toggleSort(col: 'weight' | 'return' | 'contribution' | 'mr') {
  if (sortBy.value === col) {
    sortDir.value = sortDir.value === 'desc' ? 'asc' : 'desc'
  } else {
    sortBy.value = col
    sortDir.value = 'desc'
  }
}

function sortIndicator(col: string): string {
  if (sortBy.value !== col) return ''
  return sortDir.value === 'desc' ? ' ▼' : ' ▲'
}

// Filtered and sorted rows
const rows = computed(() => {
  let result = allRows.value

  const dir = sortDir.value === 'desc' ? -1 : 1
  const key = sortBy.value
  return [...result].sort((a, b) => {
    const aVal = key === 'weight' ? (a.weightPct ?? 0)
      : key === 'return' ? (a.return_pct ?? 0)
      : key === 'mr' ? (a.imrZ ?? 0)
      : (a.contribution_pct ?? 0)
    const bVal = key === 'weight' ? (b.weightPct ?? 0)
      : key === 'return' ? (b.return_pct ?? 0)
      : key === 'mr' ? (b.imrZ ?? 0)
      : (b.contribution_pct ?? 0)
    return (aVal - bVal) * dir
  })
})

// Formatters
const fmtPct = (v: number | null | undefined, decimals = 2) => {
  if (v == null) return '--'
  return (v * 100).toFixed(decimals) + '%'
}

const fmtPrice = (v: number | null | undefined) => {
  if (v == null) return '--'
  return v.toFixed(2)
}

// NAV-share column ("£/unit", Task 2 2026-09-05 — supersedes the Arc A
// "£ WT%" column entirely, not just its label). Each cell is a genuine
// share of the fund's current per-unit Synth NAV, built so the column sums
// EXACTLY to that NAV (anchor × (1 + overnight_return_gbp)) by construction:
// BGA/GGE get a synthetic "Cash / unmapped" row for the uncovered weight
// remainder; gold gets a synthetic "TER drag" row for the daily drag it
// would otherwise omit. See docs/plans/2026-09-05-nav-share-column for the
// full derivation. '--' render convention matches fmtPct/fmtPrice, not
// formatOverlayPct's em-dash (that one's for the K_t overlay surfaces only).
const fmtNavSharePence = (pence: number | null | undefined): string => {
  if (pence == null) return '--'
  return pence.toFixed(2) + 'p'
}

// Official-NAV anchor for the £/unit column — today_official_nav_gbp
// falling back to prev_official_nav_gbp, read from whichever enriched
// payload the active fund actually populates (BGA/GGE: `enriched`; GOLD:
// `goldEnriched`, since pollData() — which sets `enriched` — is never
// called in gold mode). Both fields are in POUNDS (see ChartView.vue's
// "payload's prev_official_nav_gbp is always POUNDS" comment, and
// useSignalPipeline.ts:232-240's explicit ×100 conversion) — the NAV-share
// cells are pence, so the anchor must be converted pounds->pence here.
//
// 0-sentinel fix (fund-agnostic, live-verification fix round 2, 2026-09-09):
// /api/synth-nav/enriched emits today_official_nav_gbp as the literal
// number 0 (not null/absent) before today's official NAV has landed for
// ANY fund — `??` only falls through on null/undefined, so `0 ?? prev`
// evaluates to `0`, not `prev`, and every £/unit cell went dark
// pre-landing. Each candidate now gets its own >0 check before falling
// through to the next, so a `0` today correctly defers to prev (and a `0`
// prev correctly falls through to null) instead of being treated as a
// real (zero) anchor.
const navAnchorPence = computed<number | null>(() => {
  const src = isGold.value ? goldEnriched.value : enriched.value
  const today = src?.today_official_nav_gbp ?? null
  const prev = src?.prev_official_nav_gbp ?? null
  const pounds = (today != null && today > 0) ? today : (prev != null && prev > 0) ? prev : null
  return pounds != null ? pounds * 100 : null
})

// £/unit column visibility — BGA/GGE under hasEnriched (return_pct/
// gbpusd_rate/gbpusd_ref all live on that payload); GOLD under
// hasGoldEnriched (return_pct_gbp/covered_weight_frac/daily_drag_frac all
// live on the DAILY telemetry payload, NOT the 60s hybrid payload — unlike
// Arc A's £ WT%, this column no longer depends on holdingsHybrid.active at all).
const navShareColumnVisible = computed(() => (isGold.value ? hasGoldEnriched.value : hasEnriched.value))

// BGA/GGE: GBPUSD drift since the anchor rate, applied per-holding under the
// algebraic identity (all-USD basket ⇒ per-holding FX = aggregate FX) —
// see the arc's reference doc. Null when either rate is missing OR the ref
// rate is stale (fix-wave, 2026-09-05 reviewer catch): signal_core/synth_nav.py
// :924-934 suppresses overnight_return_gbp and falls back to a USD-space
// return once gbpusd_ref_date is >4 days old, so during that FX-outage
// window the hero card's Synth NAV is no longer anchor×(1+overnight_return_gbp)
// in GBP-FX terms. Computing per-row cells off the stale ref regardless
// would silently break the sum=Synth-NAV identity this arc exists to
// guarantee — mirror the backend's own suppression here rather than
// re-deriving staleness client-side.
const bgaGbpusdChangeFrac = computed<number | null>(() => {
  const src = enriched.value
  if (!src) return null
  if (src.gbpusd_ref_stale) return null
  const now = src.gbpusd_rate ?? null
  const ref = src.gbpusd_ref ?? null
  return now != null && ref != null && ref !== 0 ? now / ref - 1 : null
})

// BGA/GGE per-row £/unit cell: weight × (1 + r_gbp) × anchor, where
// r_gbp = (1 + return_pct) / (1 + gbpusd_change) - 1. Null (⇒ '--') if any
// input is missing.
function navSharePenceBga(h: { weightPct: number | null; return_pct: number | null }): number | null {
  const anchor = navAnchorPence.value
  const gbpusdChange = bgaGbpusdChangeFrac.value
  if (anchor == null || gbpusdChange == null || h.weightPct == null || h.return_pct == null) return null
  const rGbp = (1 + h.return_pct) / (1 + gbpusdChange) - 1
  return (h.weightPct / 100) * (1 + rGbp) * anchor
}

// BGA/GGE: fraction of NAV weight NOT covered by a holdings_detail row —
// the synthetic "Cash / unmapped" row's weight. Null when the enriched feed
// itself is unavailable (no rows to sum over at all).
const bgaCashUnmappedFrac = computed<number | null>(() => {
  if (!hasEnriched.value) return null
  let covered = 0
  for (const r of allRows.value) {
    if (r.weightPct != null) covered += r.weightPct / 100
  }
  return 1 - covered
})

// Also gated on bgaGbpusdChangeFrac (fix-wave, 2026-09-05): during an
// FX-outage window every per-row cell is '--' (see bgaGbpusdChangeFrac's
// comment) — showing a confident cash-row pence figure alongside those
// blanks would be a partial, misleading number with nothing to reconcile
// against, so the cash row goes '--' too rather than rendering in
// isolation. The row itself still renders (its weight % is independent of
// FX) — only its pence cell blanks out.
const bgaCashRowPence = computed<number | null>(() => {
  const anchor = navAnchorPence.value
  const frac = bgaCashUnmappedFrac.value
  const gbpusdChange = bgaGbpusdChangeFrac.value
  return anchor != null && frac != null && gbpusdChange != null ? frac * anchor : null
})

// Gold per-row £/unit cell: (weight / covered_weight_frac) × (1 + return_pct_gbp)
// × anchor — the /covered_weight_frac renormalisation is what makes the
// column identity hold once the TER-drag row is added (see goldTerDragPence).
// NOTE the two distinct "covered weight" fields on this page: this reads
// goldEnriched.value.covered_weight_frac (snake_case) — the DAILY K_t
// engine's coverage fraction from synth_nav:gold:enriched (Task 1), the
// same input compute_kt_return renormalises overnight_return_gbp by. This
// is NOT holdingsHybridLatest.value.coveredWeightFrac (camelCase, used by
// deriveHoldingsHybrid/hybridFooterCoveredPct) — that one is the 60s hybrid
// publisher's live/proxy coverage, a different payload and a different
// number; don't conflate the two when touching this function.
function navSharePenceGold(h: { weightPct: number | null; return_pct_gbp: number | null }): number | null {
  const anchor = navAnchorPence.value
  const covered = goldEnriched.value?.covered_weight_frac ?? null
  if (anchor == null || covered == null || covered === 0 || h.weightPct == null || h.return_pct_gbp == null) {
    return null
  }
  return (h.weightPct / 100 / covered) * (1 + h.return_pct_gbp) * anchor
}

// Gold synthetic "TER drag" row: -daily_drag_frac × anchor (negative —
// reduces the column sum). No cash/unmapped row for gold — the
// covered_weight_frac renormalisation above already accounts for uncovered
// weight internally.
const goldTerDragPence = computed<number | null>(() => {
  const anchor = navAnchorPence.value
  const drag = goldEnriched.value?.daily_drag_frac ?? null
  return anchor != null && drag != null ? -drag * anchor : null
})

// GGE per-row £/unit cell (enriched-parity arc, §7C/§7D two-term identity):
// (weight / covered_weight_frac) × (1 + return_pct_gbp) × anchor — same
// shape as navSharePenceGold, but reads `enriched` (GGE's daily-cadence
// payload via synth_nav:gge:enriched), not `goldEnriched`. Deliberately
// does NOT fold in bgaGbpusdChangeFrac's single aggregate FX change like
// navSharePenceBga does — return_pct_gbp is already the per-holding
// GBP-converted return (handles the HKD peg per-holding via
// ComputeReturnPctGbp; a single aggregate USD/GBP change would be wrong for
// the HKD names in the basket). NO GGE cash row — the covered_weight_frac
// renormalisation absorbs the uncovered weight instead (mirrors gold's
// cash-row-drop rationale exactly).
function navSharePenceGge(h: { weightPct: number | null; return_pct_gbp: number | null }): number | null {
  const anchor = navAnchorPence.value
  const covered = enriched.value?.covered_weight_frac ?? null
  if (anchor == null || covered == null || covered === 0 || h.weightPct == null || h.return_pct_gbp == null) {
    return null
  }
  return (h.weightPct / 100 / covered) * (1 + h.return_pct_gbp) * anchor
}

// GGE synthetic "TER drag" row: -daily_drag_frac × anchor. Mirrors
// goldTerDragPence but reads `enriched.daily_drag_frac` (GGE's own shim
// constant, GgeDailyDragFrac = 0.000027 — see PR-B). Null for BGA/JH (their
// enriched payload never populates daily_drag_frac) — zero regression.
const ggeTerDragPence = computed<number | null>(() => {
  const anchor = navAnchorPence.value
  const drag = enriched.value?.daily_drag_frac ?? null
  return anchor != null && drag != null ? -drag * anchor : null
})

// tfoot £/unit sum — BGA/JH: Σ per-row cells + the cash row; GOLD: Σ per-row
// cells + the TER-drag row; GGE: Σ per-row cells (navSharePenceGge) + the
// TER-drag row (no cash row — see navSharePenceGge). Skips rows whose cell
// is null (matches the rest of this file's null-tolerant sum convention)
// rather than collapsing the whole sum to null on a single missing input.
const navShareSumPence = computed<number | null>(() => {
  let sum = 0
  let any = false
  if (isGold.value) {
    for (const r of sortedGoldRows.value) {
      const v = navSharePenceGold(r)
      if (v != null) { sum += v; any = true }
    }
    const drag = goldTerDragPence.value
    if (drag != null) { sum += drag; any = true }
  } else if (isGge.value) {
    for (const h of allRows.value) {
      const v = navSharePenceGge(h)
      if (v != null) { sum += v; any = true }
    }
    const drag = ggeTerDragPence.value
    if (drag != null) { sum += drag; any = true }
  } else {
    for (const h of allRows.value) {
      const v = navSharePenceBga(h)
      if (v != null) { sum += v; any = true }
    }
    const cash = bgaCashRowPence.value
    if (cash != null) { sum += cash; any = true }
  }
  return any ? sum : null
})

const truncName = (name: string, max = 16) =>
  name.length > max ? name.slice(0, max) : name

const returnClass = (v: number | null | undefined) => {
  if (v == null) return ''
  return v > 0 ? 'positive' : v < 0 ? 'negative' : ''
}

// NAV header computeds removed — NAV hero now lives in SignalPanel

// imrGaugePosition removed — momentum uses arrows not gauge

function momentumArrow(z: number | null): string {
  if (z == null) return '--'
  const up = '\u2191', dn = '\u2193'
  const a = Math.abs(z)
  const n = a > 0.8 ? 5 : a > 0.5 ? 4 : a > 0.3 ? 3 : a > 0.1 ? 2 : 1
  return (z >= 0 ? up : dn).repeat(n)
}

function momentumClass(z: number | null): string {
  if (z == null) return 'mom-none'
  const a = Math.abs(z)
  const level = a > 0.8 ? 5 : a > 0.5 ? 4 : a > 0.3 ? 3 : a > 0.1 ? 2 : 1
  return z >= 0 ? `mom-${level}-up` : `mom-${level}-down`
}

// Rebalance banner: show when BG top-10 composition changed on the most recent
// holdings pull. Hidden once the detected date is more than 30 days old so the
// banner doesn't linger between quarterly rebalances.
const rebalanceBanner = computed(() => {
  const rb = status.value?.lastRebalance
  if (!rb) return null
  const detected = new Date(rb.detectedDate + 'T00:00:00Z')
  if (isNaN(detected.getTime())) return null
  const ageDays = (Date.now() - detected.getTime()) / (1000 * 60 * 60 * 24)
  if (ageDays > 30) return null
  return {
    date: rb.detectedDate,
    added: rb.added,
    removed: rb.removed,
    ageDays: Math.floor(ageDays),
  }
})

const totalWeightPct = computed(() => {
  const sum = allRows.value.reduce((s, r) => s + (r.weightPct ?? 0), 0)
  return sum.toFixed(1) + '%'
})

// GOLD-only: totals row weight sum (HoldingsPanel GOLD mode).
const goldTotalWeightPct = computed(() => {
  const sum = sortedGoldRows.value.reduce((s, r) => s + (r.weightPct ?? 0), 0)
  return sum.toFixed(1) + '%'
})

// Aggregate RET% for the gold totals row (paolo 2026-08-16, BGA/JH parity —
// BGA's totals cell shows the basket-level overnight return). Weight-weighted
// average over the rows that HAVE a return, renormalised to covered weight —
// the same coverage convention K_t itself uses.
const goldTotalRetPct = computed<number | null>(() => {
  let wSum = 0
  let acc = 0
  for (const r of sortedGoldRows.value) {
    if (r.ret_pct == null || r.weightPct == null || r.isCash) continue
    wSum += r.weightPct
    acc += r.weightPct * r.ret_pct
  }
  return wSum > 0 ? acc / wSum : null
})

// Aggregate return for the non-gold totals row. BGA/JH publish a
// basket-level overnight_return_usd on the enriched payload — use it as-is
// so their existing cell is unchanged. GGE's daily-cadence payload has no
// such field, so mirror gold's convention: weight-weighted market_return
// over covered rows, renormalised to the covered weight.
const nonGoldTotalReturn = computed<number | null>(() => {
  if (enriched.value?.overnight_return_usd != null) {
    return enriched.value.overnight_return_usd
  }
  let wSum = 0
  let acc = 0
  for (const r of allRows.value) {
    if (r.market_return == null || r.weightPct == null) continue
    wSum += r.weightPct
    acc += r.weightPct * r.market_return
  }
  return wSum > 0 ? acc / wSum : null
})

// Row count driving the grid-container / unavailable-message split — reads
// from the fund-appropriate row source.
const hasRows = computed(() => (isGold.value ? sortedGoldRows.value.length : rows.value.length) > 0)

// liveCount removed — header now in SignalPanel

// Polling
async function pollData() {
  await whenLoaded()
  try {
    // enrichedSynthNav is fund-aware end-to-end as of sub-C T1. The JH
    // publisher emits to `jhgtl_shadow:python:synth_nav:jh:enriched` Redis
    // key; the C# endpoint reads the JH key + asset_type='JH' anchors when
    // ?fund=jh. BGA-only fields (synth_nav_source, imr_weighted_zscore, etc.)
    // remain null on JH and existing v-if guards hide their UI cleanly.
    const isJh = portfolio.value === 'jh'
    const enrichedPromise = api.enrichedSynthNav(nonGoldFund.value).catch(() => null)
    // JH-only: fetch the discriminated signal so the MR pill below has
    // mr_zscore / mr_tilt_bps. The /api/holdings/active call is fund-aware
    // (Plan 2 + 2026-06-21 HoldingsUpdateService fix) — passing fund.value
    // routes JH to holdings:jh:active in Redis.
    const signalPromise: Promise<SignalData | null> = isJh
      ? api.signal(nonGoldFund.value).catch(() => null)
      : Promise.resolve(null)
    const [enrichedData, holdingsData, signalData] = await Promise.all([
      enrichedPromise,
      api.holdingsActive(nonGoldFund.value).catch(() => null),
      signalPromise,
    ])
    if (enrichedData && (enrichedData as any).available !== false) {
      enriched.value = enrichedData
    } else {
      enriched.value = null
    }
    if (holdingsData && (holdingsData as any).available !== false && Array.isArray(holdingsData)) {
      holdings.value = holdingsData
    }
    if (signalData && signalData.fund === 'JH') {
      jhSignal.value = signalData
    } else {
      jhSignal.value = null
    }
  } catch {
    // Ignore
  } finally {
    loading.value = false
  }
}

async function pollEquityPrices() {
  try {
    const data = await api.equityPrices()
    if (data && data.available !== false) {
      // Detect tick direction per ticker
      for (const [ticker, price] of Object.entries(data.prices)) {
        const prev = prevBids[ticker]
        if (prev != null && price.bid !== prev) {
          const dir = price.bid > prev ? 'up' : 'down'
          tickFlash[ticker] = dir
          if (flashTimers[ticker]) clearTimeout(flashTimers[ticker])
          flashTimers[ticker] = setTimeout(() => { tickFlash[ticker] = null }, 1200)
        }
        prevBids[ticker] = price.bid
      }
      equityPrices.value = data
    }
  } catch {
    // Ignore — equity prices not available (no Redis/MarketData)
  }
}

async function pollStatus() {
  await whenLoaded()
  try {
    // Fund-scoped (2026-07-12): unscoped, the server binder falls back to
    // the USER's active fund — so the JH page received BGA's status and
    // rendered BGA's rebalance banner. JH now has its own weekly HL-weight
    // overlay + rebalance detector (holdings-freshness parity Task 2,
    // 2026-08-16), so lastRebalance here is JH's own detection, not BGA's —
    // no client-side change needed, the server already scopes it correctly.
    // GOLD (F2 review fix, 2026-08-16): pass the real fund.value (not
    // nonGoldFund, which coerces GOLD->BGA) so GOLD gets its OWN rebalance
    // banner via /holdings/status?fund=GOLD instead of never polling status
    // at all (onMounted previously early-returned before this ran for gold).
    const data = await api.holdingsStatus(fund.value)
    if ((data as any).available === false) {
      status.value = null
    } else {
      status.value = data
    }
  } catch {
    // Ignore
  }
}

onMounted(() => {
  // Minute tick for `now`: gold/GGE need it for PreStrikeBadge, and BGA/JH
  // need it too (the stale-asOfUtc amber branch of deriveStrikeTotal reads
  // `now`). Other funds skip the timer.
  if (isGold.value || strikeTotalFund.value) {
    nowTimer = setInterval(() => { now.value = new Date() }, 60_000)
  }
  if (isGold.value) {
    fetchGoldHoldings()
    fetchGoldEnriched()
    // F2 (review fix, 2026-08-16): GOLD previously never polled status at
    // all, so its rebalance banner (same surface BGA/JH use, gated purely
    // on `status.value` below — see rebalanceBanner) was unreachable end-
    // to-end. pollStatus() now uses fund.value directly, so this works the
    // same way BGA/JH's status poll does.
    pollStatus()
    pollEquityPrices()
    void loadKtLatest()
    void loadHoldingsHybridLatest()
    dataTimer = setInterval(fetchGoldHoldings, 5000)
    goldEnrichedTimer = setInterval(fetchGoldEnriched, 5000)
    statusTimer = setInterval(pollStatus, 30000)
    equityTimer = setInterval(pollEquityPrices, 2000)
    ktTimer = setInterval(() => { void loadKtLatest() }, 300_000)
    holdingsHybridTimer = setInterval(() => {
      // Visibility gate — skip when the tab is hidden (publisher is 60s
      // server-side, so a resumed foreground poll picks up the latest
      // slot within one interval).
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
      void loadHoldingsHybridLatest()
    }, 60_000)
    return
  }
  pollStatus()
  pollData()
  pollEquityPrices()
  statusTimer = setInterval(pollStatus, 30000)
  dataTimer = setInterval(pollData, 5000)
  equityTimer = setInterval(pollEquityPrices, 2000)
  // GGE-only: holdings-hybrid poll, same 60s visibility-gated cadence as
  // gold's above (PR-C Task C2, spec §11.3). BGA/JH also take this branch,
  // but hasHoldingsHybrid is false for them, so loadHoldingsHybridLatest()
  // no-ops on the initial call and every subsequent tick — see the
  // fund-scoping guard-rail tests in HoldingsPanel.bga.test.ts /
  // HoldingsPanel.gge.test.ts.
  if (hasHoldingsHybrid.value || strikeTotalFund.value) {
    void loadHoldingsHybridLatest()
    holdingsHybridTimer = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
      void loadHoldingsHybridLatest()
    }, 60_000)
  }
})

onUnmounted(() => {
  if (statusTimer) clearInterval(statusTimer)
  if (dataTimer) clearInterval(dataTimer)
  if (equityTimer) clearInterval(equityTimer)
  if (goldEnrichedTimer) clearInterval(goldEnrichedTimer)
  if (ktTimer) clearInterval(ktTimer)
  if (holdingsHybridTimer) clearInterval(holdingsHybridTimer)
  if (nowTimer) clearInterval(nowTimer)
})
</script>

<template>
  <section class="holdings-panel card">

    <div v-if="loading" class="loading">Loading holdings data...</div>

    <template v-else>
      <!-- Rebalance banner — surfaces BG top-10 composition changes -->
      <div v-if="rebalanceBanner" class="rebalance-banner" :title="`Detected ${rebalanceBanner.ageDays}d ago`">
        <span class="rb-icon">↻</span>
        <span class="rb-label">{{ fund }} rebalance ({{ rebalanceBanner.date }})</span>
        <span v-if="rebalanceBanner.added.length" class="rb-added">
          +{{ rebalanceBanner.added.join(', ') }}
        </span>
        <span v-if="rebalanceBanner.removed.length" class="rb-removed">
          −{{ rebalanceBanner.removed.join(', ') }}
        </span>
      </div>

      <!-- JH Mean-Reversion pill — JH equivalent of BGA's IMR breadth pill.
           The Python jh_signal payload publishes mr_zscore (instrument MR z)
           and mr_tilt_bps (the resulting tilt size). Different concept from
           BGA's IMR breadth (which is rich/cheap holding counts) but the
           closest JH-side analogue we have today. -->
      <div v-if="jhSignal && jhSignal.mr_zscore != null" class="momentum-summary" data-testid="jh-mr-row">
        <span class="mom-arrow" :class="jhSignal.mr_zscore >= 0 ? 'mom-up' : 'mom-down'" style="font-size:1.2rem">
          {{ jhSignal.mr_zscore >= 0 ? '↑' : '↓' }}
        </span>
        <span class="momentum-label">
          JH Mean Reversion ({{ (jhSignal as any).imr_window ?? '?' }}d)
          <span class="mr-sep">|</span>
          z = {{ jhSignal.mr_zscore.toFixed(2) }}
          <span v-if="(jhSignal as any).mr_tilt_bps != null">
            <span class="mr-sep">|</span>
            tilt {{ (jhSignal as any).mr_tilt_bps >= 0 ? '+' : '' }}{{ (jhSignal as any).mr_tilt_bps.toFixed(0) }} bps
          </span>
        </span>
      </div>

      <!-- Momentum Summary (IMR breadth) — BGA-only via enriched-NAV;
           hidden on JH because pollData skips the enrichedSynthNav fetch -->
      <div v-if="enriched?.imr_weighted_zscore != null" class="momentum-summary" data-testid="imr-breadth-row">
        <span class="mom-arrow" :class="momentumClass(enriched.imr_weighted_zscore)" style="font-size:1.2rem">
          {{ momentumArrow(enriched.imr_weighted_zscore) }}
        </span>
        <span class="momentum-label">
          IMR ({{ enriched.imr_window ?? '?' }}d)
          <span class="mr-sep">|</span>
          wz {{ enriched.imr_weighted_zscore?.toFixed(2) }} <span class="mom-arrow" :class="momentumClass(enriched.imr_weighted_zscore)">{{ momentumArrow(enriched.imr_weighted_zscore) }}</span>
          <span class="mr-sep">|</span>
          <span class="mom-up">{{ ((enriched.imr_breadth_rich ?? 0) * 100).toFixed(0) }}%</span> rich (extended)
          <span class="mr-sep">|</span>
          <span class="mom-down">{{ ((enriched.imr_breadth_cheap ?? 0) * 100).toFixed(0) }}%</span> cheap (reverting)
          <span
            class="info-icon"
            data-testid="imr-breakdown-toggle"
            title="IMR composition & coverage"
            @click.stop="showImrBreakdown = !showImrBreakdown"
          >i</span>
        </span>
      </div>
      <ImrBreakdownPopover
        v-if="showImrBreakdown && enriched?.imr_weighted_zscore != null"
        :imr-window="enriched.imr_window"
        :imr-weighted-zscore="enriched.imr_weighted_zscore"
        :imr-breadth-rich="enriched.imr_breadth_rich"
        :imr-breadth-cheap="enriched.imr_breadth_cheap"
        :imr-momentum-count="enriched.imr_momentum_count"
        :holdings-count="enriched.holdings_count"
        :holdings-detail="enriched.holdings_detail"
        :imr-as-of="enriched.imr_as_of"
      />

      <!-- Momentum completeness is now checked in the System Integrity panel -->

      <!-- Single holdings table -->
      <div class="grid-container" v-if="hasRows">
        <div v-if="showSrcInfo" class="src-info-panel">
          <div class="src-info-title">Price Sources &amp; Synthetic NAV Calculation</div>
          <p class="src-info-intro">
            Each BGA holding needs a current price to compute the synthetic NAV. Because BGA's underlying
            US equities trade across different sessions and liquidity conditions, the system uses a cascade
            of sources — picking the best available for each holding at any given moment.
          </p>

          <div class="src-entry">
            <span class="src-badge src-badge-live">live</span>
            <div class="src-detail">
              <div class="src-heading">Live Pepperstone CFD — streaming, sub-second</div>
              <p>
                Real-time bid/ask from Pepperstone's FIX feed, streamed via the MarketData bridge into Redis.
                The UI polls every 2 seconds, but the underlying prices tick sub-second whenever the CFD market
                is open. Used when a direct CFD equivalent of the BGA holding exists on Pepperstone (currently
                only a handful of the largest positions — NAS100, major single-stock CFDs).
              </p>
              <p>
                <strong>Return calculation:</strong> <code>(mid / prev_close) - 1</code> where mid = (bid + ask) / 2.
                The prev_close is the holding's regular-session close fetched from AlphaVantage TIME_SERIES_DAILY.
              </p>
              <p>
                <strong>When used:</strong> Whenever the Pepperstone CFD session is open (Sun 23:00 – Fri 22:00 UTC,
                nearly 24/5). This is the most accurate source since it reflects real-time market pricing.
              </p>
            </div>
          </div>

          <div class="src-entry">
            <span class="src-badge src-badge-dely">dely</span>
            <div class="src-detail">
              <div class="src-heading">Delayed intraday — AlphaVantage 15-min-delayed 5-min bar</div>
              <p>
                Today's most recent 5-minute bar fetched from AlphaVantage's <code>TIME_SERIES_INTRADAY</code>
                endpoint with <code>entitlement=delayed</code>. Subscription tier provides US stock market data
                delayed by 15 minutes, so the freshest bar is at most ~20 minutes old
                (5-min bar close + 15-min subscription delay).
              </p>
              <p>
                <strong>When used:</strong> Holdings that are not available as Pepperstone CFDs during an
                active US trading session, where AV's latest bar timestamp matches today's ET date.
                This is the normal state for ~5–10 of the portfolio's smaller names during market hours.
              </p>
              <p>
                <strong>Return calculation:</strong> a small beta-scaled NQ correction is still applied to
                bridge the ~20-minute gap between the delayed bar and the live NQ price:
                <code>price = bar_close × (1 + β × ΔNQ)</code>, where ΔNQ is the NQ change since the bar's
                timestamp. The ADJ column shows the magnitude of this correction — for
                <span class="src-inline-badge src-badge-dely">dely</span> it should be small because the
                underlying bar is already recent.
              </p>
              <p>
                Captures idiosyncratic moves (earnings, news) that a pure NQ-proxy would miss, which is the
                main reason to prefer this over <span class="src-inline-badge src-badge-prx">prx</span> whenever
                today's intraday data is available.
              </p>
            </div>
          </div>

          <div class="src-entry">
            <span class="src-badge src-badge-ah">ah</span>
            <div class="src-detail">
              <div class="src-heading">After-Hours close — AlphaVantage snapshot, no NQ adjustment</div>
              <p>
                The most recent extended-hours closing price fetched from the AlphaVantage TIME_SERIES_INTRADAY
                API with <code>extended_hours=true</code>. This is a <em>snapshot</em>, not a stream — it
                refreshes each time the synth NAV engine runs its AV fetch cycle.
              </p>
              <p>
                <strong>Return calculation:</strong> <code>(ah_price / prev_close) - 1</code>.
                No NQ carry-forward adjustment is applied — this means the price is only accurate as of the
                bar timestamp. This source appears only briefly; once NQ data is available to apply carry-forward,
                it upgrades to <span class="src-inline-badge src-badge-prx">prx</span> or
                <span class="src-inline-badge src-badge-dely">dely</span> depending on bar age.
              </p>
              <p>
                <strong>When used:</strong> Transitional state when AH data is fetched but NQ reference data
                isn't yet available to compute the proxy adjustment.
              </p>
            </div>
          </div>

          <div class="src-entry">
            <span class="src-badge src-badge-prx">prx</span>
            <div class="src-detail">
              <div class="src-heading">NQ-adjusted proxy — overnight AH close + carry-forward, ticks with NQ</div>
              <p>
                Used when the freshest available AV bar is older than today's US ET date — typically the
                overnight period when the last print is an after-hours bar from the previous session
                (ending ~20:00 ET the day before). Takes that stale price and extrapolates forward using
                NAS100 futures movement since the instrument's last trade:
              </p>
              <p class="src-formula">
                <code>proxy_price = last_bar_close &times; (1 + β × (NQ_now / NQ_at_last_trade − 1))</code>
              </p>
              <p>
                <strong>NQ_at_last_trade</strong> is the NQ mid price at the exact time the holding's bar
                was recorded (looked up from minute-resolution NQ snapshots stored in a 48-hour rolling
                buffer). <strong>NQ_now</strong> is the current live NQ price from Pepperstone. This means
                the proxy price <em>ticks every time NQ moves</em> — effectively sub-second — even though
                the base price is static.
              </p>
              <p>
                <strong>Return calculation:</strong> <code>(proxy_price / prev_close) - 1</code>.
                The ADJ column shows the carry-forward factor; a large ADJ means the proxy is extrapolating
                further from the observed price and idiosyncratic moves (earnings, news) since the last bar
                are <em>not</em> captured.
              </p>
              <p>
                <strong>When used:</strong> Two distinct scenarios:
              </p>
              <ul>
                <li>
                  <em>Overnight / pre-market / weekends</em>: the freshest AV bar is from a prior US session,
                  so the price is carried forward with NQ extrapolation.
                </li>
                <li>
                  <em>Stale Pepperstone live tick</em>: a holding was streaming <span class="src-inline-badge src-badge-live">live</span>
                  but its last tick is now older than 5 minutes (common for mid-cap CFDs with low volume).
                  The cached tick is carried forward rather than trusted as "live".
                </li>
              </ul>
              <p>
                During an active US session a non-Pepperstone holding will usually sit on
                <span class="src-inline-badge src-badge-dely">dely</span> instead.
              </p>
            </div>
          </div>

          <div class="src-entry">
            <span class="src-badge src-badge-idx">idx</span>
            <div class="src-detail">
              <div class="src-heading">Index proxy — NAS100 return as fallback, no instrument-level data</div>
              <p>
                Fallback for holdings where AlphaVantage has no extended-hours data at all (typically
                smaller or less liquid names). Assumes the holding moved in line with NAS100 overnight:
              </p>
              <p class="src-formula">
                <code>return = (NQ_now / NQ_prev_close) - 1</code>
              </p>
              <p>
                No instrument-specific price is used — the holding's contribution to synth NAV is simply
                its portfolio weight multiplied by the NAS100 overnight return. This is a crude approximation
                but reasonable for a diversified US equity portfolio.
              </p>
              <p>
                <strong>Important:</strong> Index-proxy holdings do <strong>not</strong> count toward the
                Coverage percentage. Coverage only reflects holdings with actual instrument-level prices
                (<span class="src-inline-badge src-badge-live">live</span>,
                <span class="src-inline-badge src-badge-ah">ah</span>, or
                <span class="src-inline-badge src-badge-prx">prx</span>).
                Coverage is a monitoring metric — it does not affect the signal calculation.
                Higher coverage = more reliable synthetic NAV.
              </p>
            </div>
          </div>

          <div class="src-entry">
            <span class="src-badge src-badge-unavail">--</span>
            <div class="src-detail">
              <div class="src-heading">Unavailable — no price data</div>
              <p>
                No price data could be obtained for this holding from any source. The holding is excluded
                from the synthetic NAV calculation entirely. This typically occurs for very recently added
                holdings or during AlphaVantage API rate-limit periods.
              </p>
            </div>
          </div>

          <div class="src-entry src-entry-note">
            <span class="src-badge src-badge-note">ADJ</span>
            <div class="src-detail" v-if="isGge">
              <!-- GGE (PR-C Task C2 fix round 1): ADJ here is the
                   holdings-hybrid PRXY projection, not the NQ carry-forward
                   figure below — GGE's ADJ cell is fed by the hybrid
                   publisher's beta x crude-oil drift, never NAS100. -->
              <div class="src-heading">The ADJ (Adjustment) column — GGE hybrid proxy</div>
              <p>
                Shows the holdings-hybrid publisher's per-holding proxy adjustment: the last real
                EQUS.MINI print carried forward, scaled by that holding's beta to the crude-oil (CL)
                move since then — <code>beta &times; (CL_now / CL_at_last_print - 1)</code>. For example,
                ADJ = -0.76% means crude has drifted enough since the last real print to mark this
                holding down accordingly.
              </p>
              <p>
                A grey dot on the ticker means the row is proxied this way; a green dot means it's
                ticking live off the EQUS.MINI tape directly, in which case ADJ is blank.
              </p>
            </div>
            <div class="src-detail" v-else>
              <div class="src-heading">The ADJ (Adjustment) column</div>
              <p>
                Shows the NQ carry-forward delta applied to
                <span class="src-inline-badge src-badge-dely">dely</span> and
                <span class="src-inline-badge src-badge-prx">prx</span> holdings:
                <code>(NQ_now / NQ_at_last_trade) - 1</code>. For example, ADJ = +0.15% means NQ has
                risen 15bps since this holding's last trade — the price has been marked up accordingly.
                A large absolute ADJ value means more reliance on NQ extrapolation and is typical for
                <span class="src-inline-badge src-badge-prx">prx</span> (overnight gap) but unusual for
                <span class="src-inline-badge src-badge-dely">dely</span> (~20-minute gap).
                ADJ is blank for <span class="src-inline-badge src-badge-live">live</span> and
                <span class="src-inline-badge src-badge-idx">idx</span> sources.
              </p>
            </div>
          </div>
        </div>
        <div v-if="showAdjInfo && isGge" class="src-info-panel">
          <!-- GGE (PR-C Task C2 fix round 1): fund-aware ADJ explainer — the
               NQ/NAS100 copy below describes BGA/JH's carry-forward, which
               is unrelated to GGE's hybrid crude-beta proxy. -->
          <div class="src-info-title">ADJ — Holdings-Hybrid PRXY Projection</div>
          <p>
            Shows the beta-scaled crude-oil (CL) drift applied to proxied holdings since their last
            real EQUS.MINI print: <code>beta &times; (CL_now / CL_at_last_print - 1)</code>
          </p>
          <p>
            <strong>When ADJ shows a value:</strong> The holdings-hybrid publisher hasn't seen a real
            EQUS.MINI print for this ticker recently (grey dot), so it's carrying the last real price
            forward, adjusted by that holding's beta to crude. A larger ADJ means more reliance on the
            crude-beta extrapolation.
          </p>
          <p>
            <strong>When ADJ shows --:</strong> The holding is ticking live off the EQUS.MINI tape
            (green dot, no adjustment needed), or the holdings-hybrid publisher doesn't cover this
            ticker at all.
          </p>
        </div>
        <div v-if="showAdjInfo && !isGge" class="src-info-panel">
          <div class="src-info-title">ADJ — NQ Carry-Forward Adjustment</div>
          <p>
            Shows how much NAS100 futures have moved since each holding's price was last fetched:
            <code>(NQ_now / NQ_at_fetch) - 1</code>
          </p>
          <p>
            <strong>When ADJ shows a value:</strong> The holding's price is stale (from AlphaVantage after-hours snapshot)
            and NQ has moved meaningfully since. The proxy price is adjusted by the NQ delta to estimate the current value.
            A larger ADJ means the proxy relies more on the NQ extrapolation and less on the actual stock price.
            ADJ values are most visible overnight and pre-market when AV prices are hours old and NQ futures have moved.
          </p>
          <p>
            <strong>When ADJ shows --:</strong> Either the holding has a live Pepperstone price (no adjustment needed),
            or the NQ adjustment is less than 5bps (0.05%) — too small to be meaningful. During regular market hours,
            most holdings show -- because prices are current. The 8 extended-hours holdings
            (NVDA, AMZN, META, TSLA, GOOG, NFLX, SNOW, SHOP) tick 24/5 and will typically always show --.
          </p>
        </div>
        <div v-if="showMomInfo" class="src-info-panel">
          <div class="src-info-title">MOM — Instrument-Level Momentum</div>
          <p>
            Each holding's momentum is measured as a <strong>beta-adjusted z-score</strong> against NAS100 over a rolling window
            (default 10 days). The z-score captures whether the holding is outperforming (positive) or underperforming (negative)
            relative to what NAS100 movements would predict.
          </p>
          <p>
            <strong>Per-holding arrows:</strong>
          </p>
          <ul style="margin: 0.3rem 0 0.5rem 1.2rem; font-size: 0.8rem;">
            <li><span style="color:#3fb950;font-weight:bold">&uarr;&uarr;</span> Strong momentum (wz &gt; 0.5) — holding is significantly outperforming NAS100</li>
            <li><span style="color:#3fb950;font-weight:bold">&uarr;</span> Moderate momentum (0 &lt; wz &lt; 0.5) — holding is mildly outperforming</li>
            <li><span style="color:#f85149;font-weight:bold">&darr;</span> Losing momentum (-0.5 &lt; wz &lt; 0) — holding is mildly underperforming</li>
            <li><span style="color:#f85149;font-weight:bold">&darr;&darr;</span> Strong downdraft (wz &lt; -0.5) — holding is significantly underperforming</li>
          </ul>
          <p>
            <strong>Aggregate signal:</strong> The weighted z-score (wz) across all holdings determines the basket's overall momentum.
            When wz &gt; 0, the majority of the portfolio (by weight) has positive momentum — growth stocks are rallying relative to NAS100.
          </p>
          <p>
            <strong>How it influences the decision:</strong> When IMR Momentum mode is active, the strategy only BUYs when the aggregate
            wz &gt; 0 (basket has positive momentum). On days when wz &le; 0 (growth stocks are in a downdraft), the strategy stays in cash
            even if the NAS100 signal says BUY. This avoids investing on days when the fund's holdings are underperforming the index.
          </p>
          <p>
            <strong>Why momentum, not mean reversion:</strong> BGA is a concentrated growth fund (AMZN, META, NVDA, TSLA etc.).
            Growth stocks exhibit <em>momentum</em> — when they're outperforming, they tend to continue. The data shows a +0.27 correlation
            between wz and next-day returns: "rich" days average +0.41% while "cheap" days average -0.23%. This is the opposite of
            mean reversion, and reflects how sector rotation and risk-on/risk-off sentiment persist across multiple days.
          </p>
          <p>
            <strong>OOS impact:</strong> Adding the momentum filter improved Sharpe from 2.75 to 4.09 while reducing max drawdown
            from 9.5% to 5.4%. All 5 walk-forward folds independently chose momentum mode.
          </p>
        </div>
        <table class="holdings-table">
          <thead>
            <tr>
              <th class="col-ticker">TICKER</th>
              <th v-if="!isGold || hasGoldEnriched" class="col-dir"></th>
              <th class="col-name desktop-only">NAME</th>
              <th class="col-num col-sortable" :class="{ 'col-sorted': sortBy === 'weight' }" title="Portfolio weight — click to sort" @click="toggleSort('weight')">WT%{{ sortIndicator('weight') }}</th>
              <th class="col-num col-price" title="Bid price — live from Pepperstone or estimated from after-hours/close">BID</th>
              <th class="col-num col-price" title="Ask price — live from Pepperstone or estimated from after-hours/close">ASK</th>
              <th class="col-num col-price" title="Bid-ask spread in basis points — live or estimated">SPR</th>
              <template v-if="!isGold && hasEnriched">
                <th class="col-num col-sortable" :class="{ 'col-sorted': sortBy === 'return' }" title="Return from official NAV baseline — click to sort" @click="toggleSort('return')">RET%{{ sortIndicator('return') }}</th>
                <th class="col-src">SRC <span class="info-icon" @click.stop="showSrcInfo = !showSrcInfo">i</span></th>
                <!-- VEN/CCY (enriched-parity arc, §7C): GGE-only — venue/
                     currency resolved via /api/holdings/active?fund=gge
                     (see activeByTicker), threaded onto h.venue/h.currency
                     below. BGA/JH have neither field so stay hidden. -->
                <th v-if="isGge" class="col-num" title="Listing venue">VEN</th>
                <th v-if="isGge" class="col-num" title="Trading currency">CCY</th>
                <th class="col-num">ADJ <span class="info-icon" @click.stop="showAdjInfo = !showAdjInfo">i</span></th>
                <!-- £/unit (NAV-share column arc, Task 2 2026-09-05): this
                     holding's genuine share of the fund's current per-unit
                     Synth NAV — see navAnchorPence/navSharePenceBga. Gated
                     the same way ADJ is (hasEnriched), right of it. -->
                <th class="col-num" title="Share of current Synth NAV per unit of fund">£/unit</th>
                <th class="col-mom col-sortable" :class="{ 'col-sorted': sortBy === 'mr' }" title="Momentum — basket vs NAS100 (click to sort)" @click="toggleSort('mr')">MOM{{ sortIndicator('mr') }} <span class="info-icon" @click.stop="showMomInfo = !showMomInfo">i</span></th>
              </template>
              <template v-if="isGold && hasGoldEnriched">
                <th class="col-num col-sortable" :class="{ 'col-sorted': sortBy === 'return' }" title="Live price vs T-1 reference close (databento/PRXY hybrid feed, falling back to GBP-basis telemetry) — click to sort" @click="toggleSort('return')">RET%{{ sortIndicator('return') }}</th>
                <th class="col-mom col-sortable" :class="{ 'col-sorted': sortBy === 'mr' }" title="Momentum — per-holding z-score vs the gold basket's K_t synth NAV (daily cadence) — click to sort" @click="toggleSort('mr')">MOM{{ sortIndicator('mr') }}</th>
              </template>
              <!-- SRC (Task 2, 2026-09-04): unified with BGA's SRC column —
                   same live/dely/prx/ah/idx text + src-* colour classes.
                   Unconditional for gold (like VEN/CCY) — h.src is derived
                   from the hybrid publisher or the live-quote heuristic, not
                   from the daily hasGoldEnriched telemetry, so it doesn't
                   need that gate. No info-icon here (kept BGA-only; its
                   legend content is BGA-centric — see brief). -->
              <th v-if="isGold" class="col-src">SRC</th>
              <th v-if="isGold" class="col-num" title="Listing venue">VEN</th>
              <th v-if="isGold" class="col-num" title="Trading currency">CCY</th>
              <!-- Gold holdings-hybrid ADJ column (2026-09-04, PR C Task 10):
                   only present when the 60s hybrid publisher is live and its
                   payload is fresh (< 15 min). Mobile-visible, same as
                   BGA's ADJ column (Task 2 unification, 2026-09-04). -->
              <th v-if="isGold && holdingsHybrid.active" class="col-num"
                  title="Live↔proxy per-holding adjustment (signed, 2dp)">ADJ</th>
              <!-- £/unit (NAV-share column arc, Task 2 2026-09-05): this
                   holding's genuine share of the fund's current per-unit
                   Synth NAV. Gated on the DAILY telemetry payload
                   (hasGoldEnriched), NOT holdingsHybrid.active — unlike Arc A's
                   £ WT%, return_pct_gbp/covered_weight_frac live on
                   synth_nav:gold:enriched, not the 60s hybrid payload. -->
              <th v-if="isGold && hasGoldEnriched" class="col-num"
                  title="Share of current Synth NAV per unit of fund">£/unit</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="!isGold">
              <tr v-for="(h, i) in rows" :key="h.ticker || i"
                  :class="{ 'row-alt': i % 2 === 1 }">
                <td class="col-ticker">
                  <!-- GGE holdings-hybrid dot override (PR-C Task C2,
                       2026-09-08 \u2014 ports gold's Task 10 mechanism onto the
                       BGA/JH/GGE row template, since GGE has no monthly
                       composition snapshot to render via sortedGoldRows):
                       when the 60s hybrid publisher is live and covers this
                       ticker, its per-holding state ('live'|'prxy') wins
                       over the pre-hybrid equityPrices heuristic below.
                       hybridRowFor is null for BGA/JH (holdingsHybridLatest
                       never populated for them), so this is a no-op there. -->
                  <span class="live-dot"
                        :class="hybridRowFor(h.ticker)
                          ? hybridDotClass(hybridRowFor(h.ticker)!.state)
                          : ((h.live || (h.ticker === '_INDEX_PROXY' && enriched?.nq_price != null)) ? 'dot-live' : 'dot-stale')"
                        :title="hybridRowFor(h.ticker)
                          ? (hybridRowFor(h.ticker)!.state === 'live'
                              ? 'Live \u2014 holdings-hybrid publisher'
                              : hybridRowFor(h.ticker)!.state === 'delayed'
                                ? 'Delayed quote (~15 min) rolled by beta \u00b7 \u0394 \u2014 holdings-hybrid publisher'
                                : 'Proxied \u2014 beta \u00b7 \u0394 from the holdings-hybrid publisher')
                          : (h.live ? 'Live Pepperstone price' : h.ticker === '_INDEX_PROXY' ? 'NAS100 index proxy' : 'Stale / estimated price')"></span>
                  <span v-if="h.tier" class="tier-badge" :class="'tier-' + h.tier" :title="h.tier === 'K' ? 'Known (BG top-10)' : h.tier === 'L' ? 'Likely (demoted from top-10)' : 'Unknown (NAS100 proxy)'">{{ h.tier }}</span>
                  {{ h.ticker === '_INDEX_PROXY' ? 'PROXY' : (h.ticker || '\u2014') }}
                </td>
                <td class="col-dir">
                  <span v-if="h.direction === 1" class="dir-up" title="Trading above previous close">&#9650;</span>
                  <span v-else-if="h.direction === -1" class="dir-down" title="Trading below previous close">&#9660;</span>
                </td>
                <td class="col-name desktop-only">{{ h.ticker === '_INDEX_PROXY' ? 'Unknown Holdings Proxy' : truncName(h.name || '') }}</td>
                <td class="col-num">{{ h.weightPct != null ? h.weightPct.toFixed(1) + '%' : '--' }}</td>
                <td class="col-num col-price" :class="{ 'tick-up': tickFlash[h.ticker] === 'up', 'tick-down': tickFlash[h.ticker] === 'down' }">{{ fmtPrice(h.bid) }}</td>
                <td class="col-num col-price" :class="{ 'tick-up': tickFlash[h.ticker] === 'up', 'tick-down': tickFlash[h.ticker] === 'down' }">{{ fmtPrice(h.ask) }}</td>
                <td class="col-num col-price">{{ h.spread_bps != null ? h.spread_bps : '--' }}</td>
                <template v-if="hasEnriched">
                  <td class="col-num">
                    <div :class="returnClass(h.return_pct)">
                      {{ h.return_pct != null ? (h.return_pct >= 0 ? '+' : '') + (h.return_pct * 100).toFixed(2) + '%' : '--' }}
                    </div>
                    <div v-if="h.market_return != null" class="mkt-return" :class="h.market_return >= 0 ? 'mkt-pos' : 'mkt-neg'">
                      mkt {{ h.market_return >= 0 ? '+' : '' }}{{ (h.market_return * 100).toFixed(2) }}%
                    </div>
                    <div v-if="h.ah_return != null" class="mkt-return ah-return" :class="h.ah_return >= 0 ? 'mkt-pos' : 'mkt-neg'"
                         title="After-hours / proxy drift since the last session close">
                      ah {{ h.ah_return >= 0 ? '+' : '' }}{{ (h.ah_return * 100).toFixed(2) }}%
                    </div>
                  </td>
                  <!-- SRC (Task D1 Part 2, 2026-09-08 fix): when the
                       holdings-hybrid publisher covers this ticker (GGE
                       only — hybridRowFor is null for BGA/JH, same no-op
                       null-chain as the dot/ADJ overrides above), its
                       live/prxy state wins over the pre-hybrid h.source
                       heuristic — same override precedent as the ticker
                       dot. Reuses gold's own src-live/src-proxy colour
                       classes (see the gold branch's SRC cell below) rather
                       than inventing new styles. -->
                  <td class="col-src" :class="hybridRowFor(h.ticker) ? '' : ('src-' + h.source)">
                    <span v-if="hybridRowFor(h.ticker)"
                          class="holdings-src-pill"
                          :class="hybridRowFor(h.ticker)!.state === 'live' ? 'src-live' : hybridRowFor(h.ticker)!.state === 'delayed' ? 'src-delayed' : 'src-proxy'"
                          data-testid="holdings-src-pill">
                      {{ hybridRowFor(h.ticker)!.state === 'live' ? 'LIVE' : hybridRowFor(h.ticker)!.state === 'delayed' ? 'DELY' : 'PRXY' }}
                    </span>
                    <template v-else>
                      {{ h.source === 'live' ? 'live' : h.source === 'delayed' ? 'dely' : h.source === 'proxy' ? 'prx' : h.source === 'after_hours' ? 'ah' : h.source === 'index_proxy' ? 'idx' : '--' }}
                    </template>
                  </td>
                  <!-- VEN/CCY (enriched-parity arc, §7C): GGE-only, mirrors
                       the header th's above. h.venue/h.currency threaded
                       through from activeByTicker (/api/holdings/active). -->
                  <td v-if="isGge" class="col-num">{{ h.venue || '--' }}</td>
                  <td v-if="isGge" class="col-num">{{ h.currency || '--' }}</td>
                  <!-- ADJ cell: pre-existing NQ carry-forward adjustment
                       (BGA/JH/GGE, unconditional under hasEnriched) —
                       overridden with the holdings-hybrid live/proxy adj%
                       when the hybrid publisher covers this ticker (GGE
                       only; hybridRowFor is always null for BGA/JH). Same
                       testid as gold's hybrid ADJ cell (PR-C Task C2) since
                       both now render the same holdingsHybrid data. -->
                  <td class="col-num"
                      :class="hybridRowFor(h.ticker)
                        ? (hybridRowFor(h.ticker)!.adjText.startsWith('-') ? 'negative' : 'positive')
                        : returnClass(h.nqAdj)"
                      :data-testid="hybridRowFor(h.ticker) ? 'holdings-adj-cell' : undefined">
                    {{ hybridRowFor(h.ticker) ? hybridRowFor(h.ticker)!.adjText : (h.nqAdj != null ? (h.nqAdj >= 0 ? '+' : '') + (h.nqAdj * 100).toFixed(2) + '%' : '--') }}
                  </td>
                  <!-- £/unit: GGE branch (navSharePenceGge — two-term
                       identity, no cash row) vs BGA/JH branch
                       (navSharePenceBga — cash-row identity). -->
                  <td class="col-num" data-testid="bga-nav-share-cell">
                    {{ fmtNavSharePence(isGge ? navSharePenceGge(h) : navSharePenceBga(h)) }}
                  </td>
                  <td class="col-mom" :title="`wz = ${h.imrZ?.toFixed(2) ?? '--'}`">
                    <span class="mom-arrow" :class="momentumClass(h.imrZ)">{{ momentumArrow(h.imrZ) }}</span>
                  </td>
                </template>
              </tr>
              <!-- Synthetic "Cash / unmapped" row (NAV-share column arc,
                   Task 2 2026-09-05): the fraction of NAV weight not covered
                   by any holdings_detail row, rendered as its own £/unit
                   cell so the column sums exactly to the Synth NAV.
                   DROPPED for GGE (enriched-parity arc §7C, mirrors gold's
                   own cash-row-drop exactly): navSharePenceGge already
                   renormalises by covered_weight_frac, so rendering this
                   row too would double-count the uncovered weight. -->
              <tr v-if="hasEnriched && !isGge && navAnchorPence != null && bgaCashUnmappedFrac != null && bgaCashUnmappedFrac > 0.0001"
                  class="cash-unmapped-row" data-testid="holdings-cash-unmapped-row">
                <td class="col-ticker">Cash / unmapped</td>
                <td class="col-dir"></td>
                <td class="col-name desktop-only"></td>
                <td class="col-num">{{ (bgaCashUnmappedFrac * 100).toFixed(1) }}%</td>
                <td class="col-num col-price"></td>
                <td class="col-num col-price"></td>
                <td class="col-num col-price"></td>
                <td class="col-num"></td>
                <td class="col-src"></td>
                <td class="col-num"></td>
                <td class="col-num" data-testid="bga-nav-share-cash-cell">{{ fmtNavSharePence(bgaCashRowPence) }}</td>
                <td class="col-mom"></td>
              </tr>
              <!-- Synthetic "TER drag" row — GGE only (enriched-parity arc
                   §7C/§7D): BGA/JH never populate daily_drag_frac on their
                   enriched payload, so this is a no-op there (zero
                   regression). GGE ships no cash row (dropped above), so
                   this is the ONLY synthetic row for GGE. testid keeps the
                   `bga-` prefix per this shared branch's existing
                   convention (bga-nav-share-*). -->
              <tr v-if="isGge && hasEnriched && ggeTerDragPence != null"
                  class="ter-drag-row" data-testid="bga-nav-share-ter-drag-row">
                <td class="col-ticker">TER drag</td>
                <td class="col-dir"></td>
                <td class="col-name desktop-only"></td>
                <td class="col-num"></td>
                <td class="col-num col-price"></td>
                <td class="col-num col-price"></td>
                <td class="col-num col-price"></td>
                <td class="col-num"></td>
                <td class="col-src"></td>
                <td v-if="isGge" class="col-num"></td>
                <td v-if="isGge" class="col-num"></td>
                <td class="col-num"></td>
                <td class="col-num" data-testid="bga-nav-share-ter-drag-cell">{{ fmtNavSharePence(ggeTerDragPence) }}</td>
                <td class="col-mom"></td>
              </tr>
            </template>
            <template v-else>
              <tr v-for="(h, i) in sortedGoldRows" :key="h.ticker || i"
                  :class="{ 'row-alt': i % 2 === 1, 'cash-row': h.isCash }">
                <td class="col-ticker">
                  <!-- Gold holdings-hybrid dot override (Task 10): when the
                       60s hybrid publisher is live and covers this ticker,
                       its per-holding state ('live'|'prxy') wins over the
                       pre-hybrid equityPrices heuristic. Otherwise the
                       existing behaviour is untouched. -->
                  <span class="live-dot"
                        :class="hybridRowFor(h.ticker)
                          ? hybridDotClass(hybridRowFor(h.ticker)!.state)
                          : (h.live ? 'dot-live' : 'dot-stale')"
                        :title="hybridRowFor(h.ticker)
                          ? (hybridRowFor(h.ticker)!.state === 'live'
                              ? 'Live \u2014 gold holdings-hybrid publisher'
                              : hybridRowFor(h.ticker)!.state === 'delayed'
                                ? 'Delayed \u2014 EODHD Toronto quote (~15 min) rolled by beta \u00b7 \u0394gold'
                                : 'Proxied \u2014 beta \u00b7 \u0394gold from the gold holdings-hybrid publisher')
                          : (h.live
                              ? (h.source === 'databento' ? 'Live price \u2014 Databento (US listings)' : 'Live price \u2014 Pepperstone fallback')
                              : h.priced_from === 'close'
                                ? 'Last session close \u2014 no quote feed (market closed)'
                                : h.bid != null
                                  ? 'Last known price \u2014 stale (market closed)'
                                  : 'No live feed \u2014 monthly composition snapshot')"></span>
                  {{ h.ticker || '\u2014' }}
                </td>
                <td v-if="hasGoldEnriched" class="col-dir">
                  <span v-if="h.ret_pct != null && h.ret_pct > 0" class="dir-up" title="Trading above the daily telemetry's prev_close">&#9650;</span>
                  <span v-else-if="h.ret_pct != null && h.ret_pct < 0" class="dir-down" title="Trading below the daily telemetry's prev_close">&#9660;</span>
                </td>
                <td class="col-name desktop-only">{{ truncName(h.name || '') }}</td>
                <td class="col-num">{{ h.weightPct != null ? h.weightPct.toFixed(1) + '%' : '--' }}</td>
                <td class="col-num col-price" :class="{ 'tick-up': tickFlash[h.ticker] === 'up', 'tick-down': tickFlash[h.ticker] === 'down' }">{{ fmtPrice(h.bid) }}</td>
                <td class="col-num col-price" :class="{ 'tick-up': tickFlash[h.ticker] === 'up', 'tick-down': tickFlash[h.ticker] === 'down' }">{{ fmtPrice(h.ask) }}</td>
                <td class="col-num col-price">{{ h.spread_bps != null ? Math.round(h.spread_bps) : '--' }}</td>
                <template v-if="hasGoldEnriched">
                  <td class="col-num">
                    <div :class="returnClass(h.ret_pct)">
                      {{ h.ret_pct != null ? (h.ret_pct >= 0 ? '+' : '') + (h.ret_pct * 100).toFixed(2) + '%' : '--' }}
                    </div>
                    <div v-if="h.mkt_return != null" class="mkt-return" :class="h.mkt_return >= 0 ? 'mkt-pos' : 'mkt-neg'"
                         title="Last completed session's return (native currency, per-venue aligned)">
                      mkt {{ h.mkt_return >= 0 ? '+' : '' }}{{ (h.mkt_return * 100).toFixed(2) }}%
                    </div>
                    <div v-if="h.ah_return != null" class="mkt-return ah-return" :class="h.ah_return >= 0 ? 'mkt-pos' : 'mkt-neg'"
                         title="Drift of the current/last-known quote vs the aligned close">
                      ah {{ h.ah_return >= 0 ? '+' : '' }}{{ (h.ah_return * 100).toFixed(2) }}%
                    </div>
                  </td>
                  <td class="col-mom" :title="`z = ${h.imr_zscore?.toFixed(2) ?? '--'}`">
                    <span class="mom-arrow" :class="momentumClass(h.imr_zscore)">{{ momentumArrow(h.imr_zscore) }}</span>
                  </td>
                </template>
                <!-- SRC (Task 2, 2026-09-04): unified with BGA's SRC cell —
                     same text/colour convention, sourced from h.src
                     (goldHoldingsRows.ts's hybrid-aware mapping). Unconditional
                     for gold — see the header comment above for why this
                     isn't nested under hasGoldEnriched. -->
                <td class="col-src" :class="'src-' + h.src">
                  {{ h.src === 'live' ? 'live' : h.src === 'delayed' ? 'dely' : h.src === 'proxy' ? 'prx' : h.src === 'after_hours' ? 'ah' : h.src === 'index_proxy' ? 'idx' : '--' }}
                </td>
                <td class="col-num">{{ h.isCash ? 'cash' : (h.venue || '--') }}</td>
                <td class="col-num">{{ h.currency || '--' }}</td>
                <!-- Gold holdings-hybrid ADJ cell (Task 10): signed % of the
                     per-holding adjustment. Empty when hybrid doesn't cover
                     this ticker. Mobile-visible, same as BGA's ADJ column
                     (Task 2 unification, 2026-09-04). -->
                <td v-if="holdingsHybrid.active" class="col-num"
                    :class="hybridRowFor(h.ticker)
                      ? ((hybridRowFor(h.ticker)!.adjText.startsWith('-')) ? 'negative' : 'positive')
                      : ''"
                    data-testid="holdings-adj-cell">
                  {{ hybridRowFor(h.ticker)?.adjText ?? '—' }}
                </td>
                <!-- £/unit (NAV-share column arc, Task 2 2026-09-05): this
                     holding's genuine share of the fund's current per-unit
                     Synth NAV — see navSharePenceGold. Gated on the DAILY
                     telemetry payload (hasGoldEnriched), not holdingsHybrid. -->
                <td v-if="hasGoldEnriched" class="col-num" data-testid="gold-nav-share-cell">
                  {{ fmtNavSharePence(navSharePenceGold(h)) }}
                </td>
              </tr>
              <!-- Synthetic "TER drag" row (NAV-share column arc, Task 2
                   2026-09-05): gold's daily TER/dividend-leakage drag,
                   rendered as a negative £/unit cell so the column sums
                   exactly to the Synth NAV. No cash/unmapped row for gold —
                   the /covered_weight_frac renormalisation in
                   navSharePenceGold already accounts for uncovered weight.
                   Column structure mirrors the header/body conditionals
                   exactly (DIR/RET%/MOM under hasGoldEnriched, ADJ under
                   holdingsHybrid.active) so cells stay aligned. -->
              <tr v-if="hasGoldEnriched && goldTerDragPence != null"
                  class="ter-drag-row" data-testid="holdings-ter-drag-row">
                <td class="col-ticker">TER drag</td>
                <td v-if="hasGoldEnriched" class="col-dir"></td>
                <td class="col-name desktop-only"></td>
                <td class="col-num"></td>
                <td class="col-num col-price"></td>
                <td class="col-num col-price"></td>
                <td class="col-num col-price"></td>
                <template v-if="hasGoldEnriched">
                  <td class="col-num"></td>
                  <td class="col-mom"></td>
                </template>
                <td class="col-src"></td>
                <td class="col-num"></td>
                <td class="col-num"></td>
                <td v-if="holdingsHybrid.active" class="col-num"></td>
                <td class="col-num" data-testid="gold-ter-drag-cell">{{ fmtNavSharePence(goldTerDragPence) }}</td>
              </tr>
            </template>
          </tbody>
          <tfoot>
            <tr v-if="!isGold" class="totals-row">
              <td class="col-ticker">TOTAL</td>
              <td class="col-dir"></td>
              <td class="col-name desktop-only">{{ rows.length }} holdings</td>
              <td class="col-num">{{ totalWeightPct }}</td>
              <td class="col-num col-price"></td>
              <td class="col-num col-price"></td>
              <td class="col-num col-price"></td>
              <template v-if="hasEnriched">
                <td class="col-num" :class="returnClass(nonGoldTotalReturn)">
                  <!-- Task 6 (2026-10-24, GGE strike-anchor promotion):
                       flip to the C# shadow strike anchor when the server
                       resolves `mode: 'shadow'` + an available payload.
                       Promoted → signed % + colour-coded dot + caption;
                       otherwise today's incumbent nonGoldTotalReturn +
                       PreStrikeBadge, byte-identical to the pre-promotion
                       render (deriveStrikeTotal returns NOT_PROMOTED). -->
                  <template v-if="strikeTotal.promoted">
                    <span :data-testid="`${strikeTotalFund}-strike-total`" :class="returnClass(strikeTotal.returnFrac)">{{ formatOverlayPct(strikeTotal.returnFrac) }}</span>
                    <span class="strike-total-badge" :class="'strike-total-badge--' + strikeTotal.badge" />
                    <div class="strike-total-caption" :data-testid="`${strikeTotalFund}-strike-total-caption`">{{ strikeTotal.caption }}</div>
                  </template>
                  <template v-else>
                    {{ fmtPct(nonGoldTotalReturn, 2) }}
                    <!-- Interim pre-strike badge (PR A, Task 1): GGE has no
                         strike-anchor yet, so this figure can be a stale
                         full-day r_kt after the strike cutoff. Self-hides for
                         BGA/JH (see PreStrikeBadge's showBadge gate). -->
                    <PreStrikeBadge :fund="fund" :now="now" />
                  </template>
                </td>
                <td class="col-src"></td>
                <td v-if="isGge" class="col-num"></td>
                <td v-if="isGge" class="col-num"></td>
                <td class="col-num"></td>
                <td class="col-num" data-testid="bga-nav-share-total">{{ fmtNavSharePence(navShareSumPence) }}</td>
                <td class="col-mom"></td>
              </template>
            </tr>
            <tr v-if="isGold && sortedGoldRows.length" class="totals-row">
              <td class="col-ticker">TOTAL</td>
              <td v-if="hasGoldEnriched" class="col-dir"></td>
              <td class="col-name desktop-only">{{ sortedGoldRows.length }} holdings</td>
              <td class="col-num">{{ goldTotalWeightPct }}</td>
              <td class="col-num col-price"></td>
              <td class="col-num col-price"></td>
              <td class="col-num col-price"></td>
              <template v-if="hasGoldEnriched">
                <td class="col-num" :class="returnClass(goldTotalRetPct)"
                    title="Weight-weighted aggregate return over covered holdings">
                  {{ goldTotalRetPct != null ? (goldTotalRetPct >= 0 ? '+' : '') + (goldTotalRetPct * 100).toFixed(2) + '%' : '--' }}
                  <!-- Interim pre-strike badge (PR A, Task 1): see brother
                       comment on the GGE/BGA/JH TOTAL row above. -->
                  <PreStrikeBadge :fund="fund" :now="now" />
                </td>
                <td class="col-mom"></td>
              </template>
              <td class="col-src"></td>
              <td class="col-num"></td>
              <td class="col-num"></td>
              <!-- Gold holdings-hybrid ADJ totals slot (Task 10): keeps the
                   totals-row column count aligned with the hybrid-active
                   header. Mobile-visible (Task 2 unification, 2026-09-04) so
                   the totals row stays column-aligned with the header/body
                   at ≤768px too. -->
              <td v-if="holdingsHybrid.active" class="col-num"></td>
              <!-- £/unit totals slot (NAV-share column arc, Task 2
                   2026-09-05): Σ per-row cells + the TER-drag row — see
                   navShareSumPence. Gated on hasGoldEnriched, not
                   holdingsHybrid.active — matches the header/body cells above. -->
              <td v-if="hasGoldEnriched" class="col-num" data-testid="gold-nav-share-total">
                {{ fmtNavSharePence(navShareSumPence) }}
              </td>
            </tr>
          </tfoot>
        </table>

        <div v-if="isGold && goldResp?.available && sortedGoldRows.length" class="footer-note">
          Composition as of {{ goldResp?.asOfDate || '--' }} — source {{ goldResp?.source || '--' }}
          ({{ goldResp?.sourceFile }}, Ninety One OEIC Series iii Interim Report).
          Auto-refreshed monthly on the 1st at 19:30 UTC; weekly factsheet
          top-10 weight refresh overlays the latest SAR snapshot. Cash sleeve
          (State Street GBP Liquidity LVNAV) is in the basket but renormalised
          out of K_t.
        </div>

        <!-- Fund-neutral holdings-hybrid "next-day indication" footer
             (2026-09-04, PR C Task 10; ported to GGE 2026-09-08, PR-C
             Task C2, spec §11.3): highest-precedence hybrid footer for any
             fund with an active hybrid publisher. Present only when the
             60s hybrid publisher is live and fresh. Spec §7 semantics
             rule — the words "next-day indication" are load-bearing and
             MUST NOT be swapped for "K_t" language, because this
             indication is the aggregation of per-holding evening-session
             drift (not K_t itself, which is the daily official baseline). -->
        <div v-if="hasHoldingsHybrid && holdingsHybrid.active && !strikeTotal.promoted" class="footer-note hybrid-footer"
             data-testid="holdings-hybrid-footer">
          next-day indication: {{ hybridFooterAggregate }}
          (covered {{ hybridFooterCoveredPct }}, live {{ hybridFooterLivePct }}<template v-if="hybridFooterDelayedPct">, delayed {{ hybridFooterDelayedPct }}</template>)
        </div>

        <!-- Gold K_t live overlay bridge (2026-09-02, gold-only — not part
             of the fund-neutral hybrid arc): the monthly-refreshed
             composition snapshot above is stale between publishes — this
             footer surfaces the live β·Δgold overlay bridging the gap, same
             numbers as GoldSignalPanel's overlay line / the P&L bridge.
             Precedence (Task 10): suppressed when the hybrid footer is
             active, so only one footer ever shows. -->
        <div v-if="isGold && goldOverlay.active && !holdingsHybrid.active" class="footer-note overlay-footer"
             data-testid="gold-holdings-overlay-footer">
          basket stale → overlay {{ overlayFooterFrac }}
          (β {{ overlayFooterBeta }} × gold {{ overlayFooterGoldMove }})
          → combined {{ overlayFooterCombined }}
        </div>

        <!-- "Basket share of NAV" footer (NAV-share column arc, Task 2
             2026-09-05 — supersedes Arc A's "Basket value (GBP-weighted)"
             footer + its "≠ Synth NAV" disclaimer entirely). Same
             navShareSumPence/navAnchorPence the tfoot's £/unit total cell
             renders — this sum now genuinely equals the Synth NAV per-unit
             figure by construction (the cash/TER-drag synthetic rows make
             the identity hold), so no disclaimer is needed. -->
        <div v-if="navShareColumnVisible" class="footer-note nav-share-footer"
             data-testid="holdings-nav-share-footer">
          Basket share of NAV: {{ fmtNavSharePence(navShareSumPence) }}
        </div>
      </div>

      <!-- Holdings comparison warnings (BG vs HL weight mismatches, missing
           names, etc.) and status errors have moved to the System Integrity
           panel at the top of the dashboard — see useIntegrityChecks. -->

      <div v-if="!hasRows && !loading" class="holdings-unavailable">
        Holdings data not available{{ isGold && goldResp?.reason ? ` (${goldResp.reason})` : '' }}
      </div>
    </template>
  </section>
</template>

<style scoped>
.holdings-panel {
  border-color: #444;
}

/* Header bar — two-column layout */
.header-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  gap: 1rem;
}

.header-metrics {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 1.5rem;
}

/* Left column: NAV hero */
.nav-hero {
  flex: 1;
}

.nav-hero-label {
  font-size: 0.65rem;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.nav-hero-row {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-top: 3px;
}

.nav-hero-value {
  font-size: 1.6rem;
  font-weight: 700;
  color: #e0e0e0;
}

.nav-hero-return {
  font-size: 1.05rem;
  font-weight: 600;
  color: #ccc;
}

.nav-hero-usd {
  font-size: 0.82rem;
  color: #888;
}

/* Right column: secondary stats */
.nav-stats {
  border-left: 1px solid #2a2a3e;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.nav-stat-label {
  font-size: 0.65rem;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.nav-stat-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
}

.nav-stat-value {
  font-size: 0.95rem;
  font-weight: 600;
  color: #ccc;
}

.nav-stat-change {
  font-size: 0.82rem;
  color: #ccc;
}

.metric-label {
  font-size: 0.9rem;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.metric-value {
  font-size: 1.0rem;
  font-weight: 600;
  color: #ddd;
}

.metric-sep {
  color: #444;
  margin: 0 0.15rem;
}

.show-all-toggle {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.8rem;
  color: #999;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.show-all-toggle input {
  accent-color: #4caf50;
  cursor: pointer;
}

/* Live/stale indicator dot */
.live-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 3px;
  vertical-align: middle;
}
.dot-live { background: #4caf50; }
.dot-stale { background: #555; }
.dot-delayed { background: #29b6f6; }

/* Mean Reversion gauge */
.mr-gauge {
  margin: 0.5rem 0;
  padding: 0.4rem 0;
}

.mr-bar {
  position: relative;
  height: 8px;
  border-radius: 4px;
  overflow: visible;
}

.mr-gradient {
  position: absolute;
  inset: 0;
  border-radius: 4px;
  background: linear-gradient(to right, #4caf50, #4caf5055 30%, #33333388 50%, #f4433655 70%, #f44336);
}

.mr-marker {
  position: absolute;
  top: 50%;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #888;
  transform: translate(-50%, -50%);
  z-index: 1;
}

.mr-labels {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.3rem;
  font-size: 0.65rem;
  color: #888;
}

.mr-label-left {
  color: #4caf50;
}

.mr-label-right {
  color: #f44336;
}

.mr-ticks {
  display: flex;
  gap: 1.2rem;
  color: #555;
}

.mr-stats {
  text-align: center;
  font-size: 0.7rem;
  color: #aaa;
  margin-top: 0.2rem;
  font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
}

.mr-sep {
  color: #444;
  margin: 0 0.3rem;
}

/* Momentum arrows */
.col-mom { width: 52px; text-align: center; padding: 0 2px; }

.mom-arrow {
  font-weight: bold;
  font-size: 0.8rem;
  letter-spacing: -3px;
}

.mom-5-up { color: #3fb950; }       /* bright green — max momentum */
.mom-4-up { color: #5cc96a; }       /* green */
.mom-1-up { color: #c8edca; }       /* faint green — barely moving */
.mom-2-up { color: #a3e4a8; }       /* light green */
.mom-3-up { color: #7dd882; }       /* medium green */
.mom-4-up { color: #5cc96a; }       /* green */
.mom-5-up { color: #3fb950; }       /* bright green — max momentum */
.mom-1-down { color: #f5c4c2; }     /* faint red — barely fading */
.mom-2-down { color: #f0a8a5; }     /* light red */
.mom-3-down { color: #f08480; }     /* medium red */
.mom-4-down { color: #f8665f; }     /* red */
.mom-5-down { color: #f85149; }     /* bright red — max fading */
.mom-none { color: #484f58; }

/* Momentum summary gauge */
.momentum-summary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0;
}

.momentum-label {
  font-size: 0.7rem;
  color: #aaa;
  font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
}

/* Holdings grid */
.grid-container {
  margin-top: 0.5rem;
}

.holdings-table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 0.95rem;
  line-height: 1.7;
}

.holdings-table thead {
}

.holdings-table thead tr {
  background: #111;
  border-bottom: 1px solid #444;
}

.holdings-table th {
  padding: 0.45rem 0.7rem;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  font-size: 0.85rem;
  letter-spacing: 0.03em;
  white-space: nowrap;
}

.holdings-table td {
  padding: 0.35rem 0.7rem;
  white-space: nowrap;
}

.holdings-table tbody tr {
  background: #1a1a1a;
}

.holdings-table tbody tr.row-alt {
  background: #1f1f1f;
}

.holdings-table tbody tr:hover {
  background: #252525;
}

.col-tier {
  width: 1.8rem;
  text-align: center;
  padding: 0 0.1rem;
}
.col-dir {
  width: 1.2rem;
  text-align: center;
  padding: 0;
}
.tier-badge {
  display: inline-block;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 0.05rem 0.25rem;
  border-radius: 2px;
  margin-left: 0.15rem;
  margin-right: 0.2rem;
  vertical-align: middle;
  cursor: help;
  line-height: 1.2;
}
.tier-K { background: #1b5e20; color: #a5d6a7; }
.tier-L { background: #e65100; color: #ffcc80; }
.tier-U { background: #37474f; color: #90a4ae; }

.dir-up {
  color: #4caf50;
  font-size: 1.2rem;
  cursor: help;
  vertical-align: middle;
  line-height: 1;
}

.dir-down {
  color: #f44336;
  font-size: 1.2rem;
  cursor: help;
  vertical-align: middle;
  line-height: 1;
}

.col-ticker {
  text-align: left;
  color: #ddd;
  font-weight: 600;
}

.col-name {
  text-align: left;
  color: #888;
}

.col-num {
  text-align: right;
  color: #ccc;
}
.col-sortable {
  cursor: pointer;
  user-select: none;
}
.col-sortable:hover {
  color: #4caf50;
}
.col-sorted {
  color: #4caf50;
}

/* Source column */
.col-src { text-align: center; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.03em; }
.src-live { color: #4caf50; }
.src-delayed { color: #29b6f6; }
.src-proxy { color: #ff9800; }
.src-after_hours { color: #ccc; }
.src-index_proxy { color: #888; }

/* Info icon (matches BacktestPanel pattern) */
.info-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #333;
  border: 1px solid #555;
  color: #aaa;
  font-size: 10px;
  font-style: italic;
  font-weight: bold;
  cursor: pointer;
  margin-left: 4px;
  vertical-align: middle;
  user-select: none;
  transition: all 0.15s;
  font-family: Georgia, serif;
}
.info-icon:hover {
  background: #444;
  color: #4caf50;
  border-color: #4caf50;
}

/* SRC info panel */
.src-info-panel {
  background: #1a2a1a;
  border: 1px solid #2a4a2a;
  border-radius: 4px;
  padding: 12px 14px;
  font-size: 11px;
  line-height: 1.6;
  color: #ccc;
  margin: 4px 0;
  animation: srcInfoFadeIn 0.15s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

@keyframes srcInfoFadeIn {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}

.src-info-title {
  font-size: 13px;
  font-weight: 700;
  color: #eee;
  margin-bottom: 6px;
}

.src-info-intro {
  margin: 0 0 10px;
  color: #aaa;
}

.src-entry {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #2a3a2a;
}

.src-entry:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.src-badge {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 20px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
  margin-top: 2px;
}

.src-badge-live { background: #1b3a1b; color: #4caf50; border: 1px solid #4caf50; }
.src-badge-dely { background: #0d2a3a; color: #29b6f6; border: 1px solid #29b6f6; }
.src-badge-ah { background: #2a2a2a; color: #ccc; border: 1px solid #666; }
.src-badge-prx { background: #3a2a00; color: #ff9800; border: 1px solid #ff9800; }
.src-badge-idx { background: #2a2a2a; color: #888; border: 1px solid #555; }
.src-badge-unavail { background: #2a2a2a; color: #555; border: 1px solid #444; }
.src-badge-note { background: #1a2a3a; color: #64b5f6; border: 1px solid #64b5f6; font-size: 9px; }

.src-inline-badge {
  display: inline;
  padding: 1px 4px;
  border-radius: 2px;
  font-size: 10px;
  font-weight: 700;
  font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
}

.src-detail {
  flex: 1;
  min-width: 0;
}

.src-heading {
  font-weight: 600;
  color: #ddd;
  margin-bottom: 3px;
  font-size: 11.5px;
}

.src-detail p {
  margin: 4px 0;
  color: #aaa;
}

.src-formula {
  background: #111;
  border: 1px solid #333;
  border-radius: 3px;
  padding: 4px 8px;
  display: inline-block;
}

.src-detail code {
  background: #222;
  padding: 1px 4px;
  border-radius: 2px;
  font-size: 10.5px;
  color: #e0e0e0;
}

/* Totals */
.holdings-table tfoot {
}

.totals-row {
  background: #111 !important;
  border-top: 1px solid #444;
  font-weight: 600;
}

/* Task 6 (2026-10-24, GGE strike-anchor promotion): TOTAL-row shadow
   badge + caption. Dot reuses the pane's existing live/stale/overlay
   colour vocabulary (dot-live green, overlay-footer amber, dot-stale
   grey, .negative red) so the four states read the same across the
   dashboard. Caption is small muted text, same tone as .footer-note. */
.strike-total-badge {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-left: 4px;
  vertical-align: middle;
}
.strike-total-badge--green { background: #4caf50; }
.strike-total-badge--amber { background: #f6c542; }
.strike-total-badge--grey  { background: #555; }
.strike-total-badge--red   { background: #ff5252; }
.strike-total-caption {
  font-size: 0.65rem;
  color: #666;
  font-weight: 400;
  line-height: 1.2;
  margin-top: 2px;
}

/* GOLD-only: cash sleeve rows de-emphasised (HoldingsPanel GOLD mode) */
.cash-row td { opacity: 0.55; }

/* NAV-share column synthetic rows (Task 2, 2026-09-05): the "Cash /
   unmapped" (BGA/GGE) and "TER drag" (GOLD) rows that make the £/unit
   column sum to the Synth NAV exactly — de-emphasised like the gold cash
   sleeve row above, distinct class since it's a different concept
   (weight/drag reconciliation, not a real holding). */
.cash-unmapped-row td,
.ter-drag-row td { opacity: 0.55; font-style: italic; }

/* GOLD-only: provenance footer (HoldingsPanel GOLD mode) */
.footer-note {
  margin-top: 0.75rem;
  font-size: 0.7rem;
  color: #666;
  line-height: 1.5;
  border-top: 1px solid #333;
  padding-top: 0.5rem;
}
/* Gold K_t live overlay bridge (2026-09-02) — sits right under the
   composition note, no extra top border needed. */
.overlay-footer {
  margin-top: 0.35rem;
  border-top: none;
  padding-top: 0;
  color: #f6c542;
}

/* "Basket share of NAV" footer (NAV-share column arc, Task 2 2026-09-05) —
   shared by BGA/GGE and GOLD (.footer-note is the general "small footer
   line" convention). Supersedes Arc A's .basket-value-footer/
   .basket-value-disclaimer — no disclaimer needed now the sum genuinely
   equals the Synth NAV by construction. */
.nav-share-footer {
  margin-top: 0.35rem;
  border-top: none;
  padding-top: 0;
}

/* Colors */
.positive {
  color: #4caf50;
}

.negative {
  color: #f44336;
}

/* Market return (vs T-1 close) — secondary line under signal return */
.mkt-return { font-size: 9px; opacity: 0.7; margin-top: 1px; }
.ah-return { opacity: 0.5; font-style: italic; }
.mkt-pos { color: #4caf50; }
.mkt-neg { color: #f44336; }

.coverage-good {
  color: #4caf50;
}

.coverage-warn {
  color: #ff9800;
}

.coverage-low {
  color: #f44336;
}

/* Misc */
.warnings-list {
  border-top: 1px solid #333;
  padding-top: 0.5rem;
  margin-top: 0.5rem;
}

.warning-item {
  font-size: 0.75rem;
  color: #ff9800;
  padding: 0.1rem 0;
}

.error-text {
  color: #f44336;
  font-size: 0.75rem;
}

.completeness-alert {
  background: #2a1a0a;
  border: 1px solid #ff9800;
  border-radius: 4px;
  padding: 4px 8px;
  margin-bottom: 6px;
  font-size: 0.75rem;
  color: #ff9800;
}

.rebalance-banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #0f1a2a;
  border: 1px solid #3a7bd5;
  border-radius: 4px;
  padding: 4px 10px;
  margin-bottom: 6px;
  font-size: 0.8rem;
  color: #9ec5ff;
}
.rebalance-banner .rb-icon {
  font-size: 1rem;
  color: #3a7bd5;
}
.rebalance-banner .rb-label {
  font-weight: 600;
  color: #cfe2ff;
}
.rebalance-banner .rb-added {
  color: #81c784;
}
.rebalance-banner .rb-removed {
  color: #e57373;
}

/* Price tick flash */
.tick-up {
  background-color: rgba(76, 175, 80, 0.3);
  transition: background-color 0.15s ease-in;
}

.tick-down {
  background-color: rgba(244, 67, 54, 0.3);
  transition: background-color 0.15s ease-in;
}

.holdings-unavailable {
  color: #666;
  font-style: italic;
  padding: 0.5rem 0;
}

.loading {
  color: #888;
  padding: 1rem 0;
}

/* ---- Mobile responsive ---- */
@media (max-width: 768px) {
  .header-bar {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-metrics {
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
  }

  .nav-hero-row {
    flex-wrap: wrap;
  }

  .nav-hero-value {
    font-size: 1.4rem;
  }

  .nav-hero-return {
    font-size: 0.95rem;
  }

  .nav-stats {
    border-left: none;
    border-top: 1px solid #2a2a3e;
    padding-left: 0;
    padding-top: 8px;
    margin-top: 10px;
    flex-direction: row;
    gap: 20px;
  }

  .nav-stat-value {
    font-size: 0.85rem;
  }

  .metric-label {
    font-size: 0.75rem;
  }

  .metric-value {
    font-size: 0.85rem;
  }

  .show-all-toggle {
    min-height: 44px;
    display: flex;
    align-items: center;
  }

  .grid-container {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin-left: -0.5rem;
    margin-right: -0.5rem;
    padding: 0 0.5rem;
  }

  .holdings-table {
    font-size: 0.75rem;
    min-width: 0;
    width: 100%;
    table-layout: fixed;
  }

  .holdings-table th {
    padding: 0.3rem 0.15rem;
    font-size: 0.65rem;
  }

  .holdings-table td {
    padding: 0.3rem 0.15rem;
    font-size: 0.7rem;
  }

  /* Tighten specific columns for mobile — table-layout:fixed needs explicit widths */
  /* Visible: TICKER(1), DIR(2), WT%(4), RET T-1(9), SRC(10), ADJ(11), MOM(12) */
  .col-ticker { width: 22%; font-size: 0.7rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .col-dir { width: 4%; padding: 0 !important; }
  .col-num { width: 12%; font-size: 0.68rem; padding: 0.2rem 0.1rem !important; }
  .col-src { width: 10%; padding: 0 0.05rem !important; font-size: 0.6rem; }
  .col-mom { width: 10%; padding: 0 0.05rem !important; }
  .tier-badge { margin-left: 0; margin-right: 0.1rem; padding: 0.02rem 0.12rem; font-size: 0.45rem; }
  .dir-up, .dir-down { font-size: 0.85rem; }

  /* Mobile: hide NAME (desktop-only) and the BID/ASK/SPR price columns
     (class-based — works for both the BGA/JH and GOLD column sets, which
     have different column counts, so nth-child position no longer applies). */
  .desktop-only {
    display: none;
  }
  .col-price {
    display: none;
  }

  .mr-ticks {
    gap: 0.5rem;
    font-size: 0.6rem;
  }

  .mr-labels {
    font-size: 0.6rem;
  }

  .mr-stats {
    font-size: 0.65rem;
  }

  .src-info-panel {
    font-size: 10px;
  }

  .src-entry {
    flex-direction: column;
    gap: 4px;
  }

  .src-badge {
    align-self: flex-start;
  }

  .mkt-return { font-size: 8px; }
}
</style>
